/**
 * Category Research Management - Super Admin Only
 *
 * Unified interface for managing AI-researched category characteristics:
 * - Product types, brand tiers, use cases, premium features, personas, tier vocabulary
 * - View freshness and status of each research aspect
 * - Trigger research refresh for stale categories
 * - Monitor research costs
 *
 * Date: January 10, 2026
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Progress } from '@/components/ui/progress';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Layers,
  RefreshCw,
  Clock,
  CheckCircle,
  AlertCircle,
  XCircle,
  Loader2,
  DollarSign,
  TrendingUp,
  Package,
  Tag,
  Users,
  Sparkles,
  MessageSquare,
  ChevronRight,
  Eye,
  AlertTriangle,
  Settings,
  Zap,
  Database,
  Link2,
  Heart,
  Edit3,
  Plus,
  Trash2,
  X,
  Save,
  Lightbulb,
  Search,
  Check,
  Wand2,
  AlertTriangle as WarningIcon,
  ArrowRight,
  ArrowLeft,
  Play,
  Square,
  CheckSquare,
  ListChecks,
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { authFetch } from '@/utils/auth-fetch';
import { useToast } from '@/components/ui/use-toast';
import { API_URL } from '@/config/api';
import { useSite } from '@/contexts/SiteContext';
import { useAuth } from '@/context/auth-context';
import { cn } from '@/lib/utils';

interface CategoryCharacteristics {
  id: string;
  sectorId: number;
  categoryPath: string;
  categoryName: string;
  status: 'pending' | 'researching' | 'active' | 'stale' | 'failed';
  aspects: {
    product_types: { hasData: boolean; count: number; data: any[] };
    brand_tiers: { hasData: boolean; tierCount: number; data: any };
    use_cases: { hasData: boolean; count: number; data: any[] };
    premium_features: { hasData: boolean; count: number; data: any[] };
    personas: { hasData: boolean; count: number; data: any[] };
    tier_vocabulary: { hasData: boolean; tierCount: number; data: any };
    price_tiers: { hasData: boolean; tierCount: number; data: any };
    spec_semantics?: { hasData: boolean; specCount: number; data: any };
    measurement_units?: { hasData: boolean; unitCount: number; data: any };
    validation_rules?: { hasData: boolean; data: any };
  };
  researchDimensions: boolean[] | null; // 7-dimension completeness array
  freshness: {
    status: 'fresh' | 'aging' | 'stale' | 'none' | 'failed' | 'researching';
    message: string;
    daysOld: number | null;
    error?: string;
  };
  lastResearchedAt: string | null;
  staleAfter: string | null;
  researchModel: string | null;
  researchCostUsd: number;
  timesUsed: number;
  lastUsedAt: string | null;
}

interface SummaryStats {
  total: number;
  byStatus: {
    active: number;
    stale: number;
    pending: number;
    failed: number;
    researching: number;
  };
  totalCostUsd: number;
  avgUsage: number;
  oldestResearch: string | null;
  newestResearch: string | null;
}

interface AvailableCategory {
  categoryPath: string;
  categoryName: string;
  productCount: number;
  brandCount: number;
  priceRange: { min: number; max: number } | null;
  hasResearch: boolean;
  researchStatus: string | null;
  lastResearchedAt: string | null;
}

interface TechnicalResearchStatus {
  categoryName: string;
  categoryPath: string;
  research: {
    canonicalAttributes: { count: number; hasData: boolean };
    attributeMappings: { count: number; hasData: boolean };
    perceivedBenefits: { count: number; hasData: boolean };
    attributeIntelligence: { count: number; hasData: boolean };
    extractionHints?: { count: number; withHints: number; hasData: boolean };
  };
}

interface ExtractionHint {
  specName: string;
  displayName: string;
  guidance: string;
  typicalRange: string;
  unit: string;
  fieldDisambiguation: {
    prefer_fields?: string[];
    avoid_fields?: string[];
    notes?: string;
  } | null;
  qualifierRules: {
    preserve?: string[];
    examples?: string[];
  } | null;
  semanticClarifications: {
    mislabels?: Record<string, string>;
    notes?: string;
  } | null;
  isMultiSelect: boolean;
  isKeyAttribute: boolean;
  priorityRank: number;
  confidenceScore: number;
  hasDisambiguation: boolean;
  hasQualifierRules: boolean;
  hasSemanticClarifications: boolean;
}

interface CanonicalAttribute {
  id: string;
  product_category: string;
  attribute_name: string;
  canonical_name: string;
  data_type: string;
  unit: string | null;
  typical_min: number | null;
  typical_max: number | null;
  enum_values: string[] | null;
  description: string | null;
  created_at: string;
  updated_at: string;
}

interface AttributeIntelligenceEntry {
  id: string;
  title: string;
  scope_attribute_name: string;
  scope_category_path: string;
  research_findings: string;
  knowledge_text: string;
  status: string;
  created_at: string;
  updated_at: string;
}

interface Sector {
  id: string;
  name: string;
  description: string;
  parent_sector_id: string | null;
  created_at: string;
  updated_at: string;
}

const ASPECT_CONFIG = {
  product_types: {
    label: 'Product Types',
    icon: Package,
    description: 'Subcategories and product type patterns',
    color: 'blue',
  },
  brand_tiers: {
    label: 'Brand Tiers',
    icon: Tag,
    description: 'Premium, mid, entry brand classifications',
    color: 'purple',
  },
  use_cases: {
    label: 'Use Cases',
    icon: TrendingUp,
    description: 'Product use case patterns and regex',
    color: 'green',
  },
  premium_features: {
    label: 'Premium Features',
    icon: Sparkles,
    description: 'Features that define premium products',
    color: 'yellow',
  },
  personas: {
    label: 'Personas',
    icon: Users,
    description: 'Buyer personas for this category',
    color: 'pink',
  },
  tier_vocabulary: {
    label: 'Tier Vocabulary',
    icon: MessageSquare,
    description: 'Language guidance per price tier',
    color: 'orange',
  },
  price_tiers: {
    label: 'Price Tiers',
    icon: DollarSign,
    description: 'Market-based price tier thresholds',
    color: 'green',
  },
};

function FreshnessIndicator({ freshness }: { freshness: CategoryCharacteristics['freshness'] }) {
  const config = {
    fresh: { color: 'bg-green-500', icon: CheckCircle, text: 'text-green-700 dark:text-green-400' },
    aging: { color: 'bg-yellow-500', icon: Clock, text: 'text-yellow-700 dark:text-yellow-400' },
    stale: { color: 'bg-orange-500', icon: AlertTriangle, text: 'text-orange-700 dark:text-orange-400' },
    none: { color: 'bg-gray-400', icon: XCircle, text: 'text-gray-500 dark:text-gray-400' },
    failed: { color: 'bg-red-500', icon: AlertCircle, text: 'text-red-700 dark:text-red-400' },
    researching: { color: 'bg-blue-500', icon: Loader2, text: 'text-blue-700 dark:text-blue-400' },
  };

  const cfg = config[freshness.status];
  const Icon = cfg.icon;

  // Fix message for negative days
  let message = freshness.message;
  if (freshness.daysOld !== null && freshness.daysOld <= 0) {
    message = 'Fresh (today)';
  }

  return (
    <div className="flex items-center gap-2">
      <div className={cn('w-2 h-2 rounded-full', cfg.color)} />
      <Icon className={cn('h-4 w-4', cfg.text, freshness.status === 'researching' && 'animate-spin')} />
      <span className={cn('text-sm', cfg.text)}>{message}</span>
    </div>
  );
}

function AspectStatus({ aspect, data, onRefresh, isRefreshing }: {
  aspect: keyof typeof ASPECT_CONFIG;
  data: any;
  onRefresh?: () => void;
  isRefreshing?: boolean;
}) {
  const [expanded, setExpanded] = useState(false);
  const config = ASPECT_CONFIG[aspect];
  const Icon = config.icon;
  const hasData = data?.hasData || false;
  const count = data?.count || data?.tierCount || 0;
  const actualData = data?.data;

  const renderDataPreview = () => {
    if (!actualData) return null;

    switch (aspect) {
      case 'product_types':
        return (
          <div className="space-y-2">
            {Array.isArray(actualData) && actualData.map((pt: any, i: number) => (
              <div key={i} className="p-2 bg-white dark:bg-gray-900 rounded border">
                <div className="font-medium text-sm">{pt.type_name?.en || pt.type_key}</div>
                {pt.keywords && <div className="text-xs text-muted-foreground">Keywords: {pt.keywords.slice(0, 5).join(', ')}</div>}
                {pt.semantic_anchors?.en ? (
                  <div className="text-xs text-green-600 dark:text-green-400 mt-1">
                    <Search className="inline h-3 w-3 mr-1" />
                    Anchors: {pt.semantic_anchors.en.slice(0, 3).join(', ')}
                  </div>
                ) : (
                  <div className="text-xs text-amber-600 dark:text-amber-400 mt-1 flex items-center gap-1">
                    <AlertTriangle className="h-3 w-3" />
                    Missing semantic anchors - needs re-research
                  </div>
                )}
              </div>
            ))}
          </div>
        );

      case 'brand_tiers':
        return (
          <div className="grid grid-cols-3 gap-2">
            {['premium', 'mid', 'entry'].map(tier => (
              <div key={tier} className="p-2 bg-white dark:bg-gray-900 rounded border">
                <div className="font-medium text-sm capitalize">{tier}</div>
                <div className="text-xs text-muted-foreground">
                  {Array.isArray(actualData[tier]) ? actualData[tier].slice(0, 5).join(', ') : '-'}
                </div>
              </div>
            ))}
          </div>
        );

      case 'use_cases':
        return (
          <div className="space-y-2">
            {Array.isArray(actualData) && actualData.map((uc: any, i: number) => (
              <div key={i} className="p-2 bg-white dark:bg-gray-900 rounded border">
                <div className="font-medium text-sm">{uc.display_name?.en || uc.use_case}</div>
                {uc.description && <div className="text-xs text-muted-foreground">{uc.description}</div>}
              </div>
            ))}
          </div>
        );

      case 'premium_features':
        return (
          <div className="space-y-2">
            {Array.isArray(actualData) && actualData.map((f: any, i: number) => (
              <div key={i} className="p-2 bg-white dark:bg-gray-900 rounded border">
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="text-xs">#{f.priority}</Badge>
                  <span className="font-medium text-sm">{f.display_label?.en || f.feature_key || f.attribute_key}</span>
                </div>
                {f.description && (
                  <div className="text-xs text-muted-foreground mt-1 line-clamp-2">{f.description}</div>
                )}
                {f.anchor_phrase?.en ? (
                  <div className="text-xs text-green-600 dark:text-green-400 mt-1">
                    <Search className="inline h-3 w-3 mr-1" />
                    Anchor: "{f.anchor_phrase.en}"
                  </div>
                ) : (
                  <div className="text-xs text-amber-600 dark:text-amber-400 mt-1 flex items-center gap-1">
                    <AlertTriangle className="h-3 w-3" />
                    Missing anchor phrase
                  </div>
                )}
              </div>
            ))}
          </div>
        );

      case 'personas':
        return (
          <div className="space-y-2">
            {Array.isArray(actualData) && actualData.map((p: any, i: number) => (
              <div key={i} className="p-2 bg-white dark:bg-gray-900 rounded border">
                <div className="font-medium text-sm">{p.name?.en || p.persona_name || p.name}</div>
                {(p.description?.en || p.description) && (
                  <div className="text-xs text-muted-foreground line-clamp-2">{p.description?.en || p.description}</div>
                )}
              </div>
            ))}
          </div>
        );

      case 'tier_vocabulary':
        return (
          <div className="grid grid-cols-2 gap-2">
            {Object.entries(actualData || {}).map(([tier, vocab]: [string, any]) => (
              <div key={tier} className="p-2 bg-white dark:bg-gray-900 rounded border">
                <div className="font-medium text-sm capitalize mb-1">{tier.replace('_', ' ')}</div>
                {vocab?.use && (
                  <div className="mb-1">
                    <span className="text-xs text-green-600 dark:text-green-400">Use:</span>
                    <div className="flex flex-wrap gap-1 mt-0.5">
                      {vocab.use.slice(0, 3).map((t: string, i: number) => (
                        <Badge key={i} variant="outline" className="text-xs bg-green-50 dark:bg-green-950/30 text-green-800 dark:text-green-300">{t}</Badge>
                      ))}
                    </div>
                  </div>
                )}
                {vocab?.avoid && (
                  <div>
                    <span className="text-xs text-red-600 dark:text-red-400">Avoid:</span>
                    <div className="flex flex-wrap gap-1 mt-0.5">
                      {vocab.avoid.slice(0, 3).map((t: string, i: number) => (
                        <Badge key={i} variant="outline" className="text-xs bg-red-50 dark:bg-red-950/30 text-red-800 dark:text-red-300">{t}</Badge>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        );

      case 'price_tiers':
        return (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {['entry', 'mid_range', 'premium', 'ultra_premium'].map((tier) => {
              const tierData = actualData?.[tier];
              if (!tierData) return null;
              return (
                <div key={tier} className="p-3 bg-white dark:bg-gray-900 rounded border">
                  <div className="font-medium text-sm capitalize mb-1">{tier.replace('_', ' ')}</div>
                  <div className="text-lg font-bold text-green-600 dark:text-green-400">
                    {tierData.max ? `≤ €${tierData.max}` : 'No limit'}
                  </div>
                  {tierData.personas?.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1">
                      {tierData.personas.slice(0, 3).map((persona: string, i: number) => (
                        <Badge key={i} variant="outline" className="text-xs">
                          {persona.replace(/_/g, ' ')}
                        </Badge>
                      ))}
                      {tierData.personas.length > 3 && (
                        <Badge variant="outline" className="text-xs">+{tierData.personas.length - 3}</Badge>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        );

      default:
        return <pre className="text-xs overflow-auto max-h-40">{JSON.stringify(actualData, null, 2)}</pre>;
    }
  };

  return (
    <div className={cn(
      'rounded-lg border overflow-hidden',
      hasData ? 'bg-green-50 border-green-200 dark:bg-green-950/20 dark:border-green-800' : 'bg-gray-50 border-gray-200 dark:bg-gray-950/20 dark:border-gray-700'
    )}>
      <div
        className={cn(
          'flex items-center justify-between p-3 cursor-pointer hover:bg-opacity-80',
          hasData && 'hover:bg-green-100 dark:hover:bg-green-900/30'
        )}
        onClick={() => hasData && setExpanded(!expanded)}
      >
        <div className="flex items-center gap-3">
          <Icon className={cn('h-5 w-5', hasData ? 'text-green-600' : 'text-gray-400')} />
          <div>
            <p className="font-medium text-sm">{config.label}</p>
            <p className="text-xs text-muted-foreground">{config.description}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {hasData ? (
            <>
              <Badge variant="secondary" className="bg-green-100 text-green-800">
                {count} {count === 1 ? 'item' : 'items'}
              </Badge>
              <CheckCircle className="h-4 w-4 text-green-600" />
              <ChevronRight className={cn('h-4 w-4 transition-transform', expanded && 'rotate-90')} />
            </>
          ) : (
            <Badge variant="outline" className="text-gray-500">No data</Badge>
          )}
          {onRefresh && (
            <Button
              size="sm"
              variant="ghost"
              onClick={(e) => { e.stopPropagation(); onRefresh(); }}
              disabled={isRefreshing}
              className="h-8 w-8 p-0"
            >
              {isRefreshing ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <RefreshCw className="h-4 w-4" />
              )}
            </Button>
          )}
        </div>
      </div>
      {expanded && hasData && (
        <div className="p-3 pt-0 border-t border-green-200 dark:border-green-800 bg-green-25 dark:bg-green-950/10">
          {renderDataPreview()}
        </div>
      )}
    </div>
  );
}

function CategoryDetailDialog({
  category,
  isOpen,
  onClose,
  onRefresh,
  onRefreshAspect,
  technicalResearch,
  onRegenerateAttributes,
  onResearchIntelligence,
  onResearchExtractionHints,
  onResearchPerceivedBenefits,
  onResearchAttributeMappings,
  sectorId,
  onOpenCanonicalDialog,
  onOpenIntelligenceDialog,
  onOpenExtractionHintsDialog,
  onOpenChatDialog,
}: {
  category: CategoryCharacteristics | null;
  isOpen: boolean;
  onClose: () => void;
  onRefresh: (categoryPath: string) => Promise<void>;
  onRefreshAspect: (categoryPath: string, aspect: string) => Promise<void>;
  technicalResearch: TechnicalResearchStatus | null;
  onRegenerateAttributes: (categoryId: string, forceRegenerate: boolean) => Promise<void>;
  onResearchIntelligence: (categoryName: string, depth?: 'quick' | 'standard' | 'deep', forceRegenerate?: boolean) => Promise<void>;
  onResearchExtractionHints: (forceRegenerate: boolean) => Promise<void>;
  onResearchPerceivedBenefits: (categoryName: string, forceRegenerate: boolean) => Promise<void>;
  onResearchAttributeMappings: (forceRegenerate: boolean) => Promise<void>;
  sectorId: number | undefined;
  onOpenCanonicalDialog: () => void;
  onOpenIntelligenceDialog: () => void;
  onOpenExtractionHintsDialog: () => void;
  onOpenChatDialog?: () => void;
}) {
  const [refreshing, setRefreshing] = useState(false);
  const [refreshingAspect, setRefreshingAspect] = useState<string | null>(null);
  const [regeneratingAttrs, setRegeneratingAttrs] = useState(false);
  const [researchingIntelligence, setResearchingIntelligence] = useState(false);
  const [researchingExtractionHints, setResearchingExtractionHints] = useState(false);
  const [researchingPerceivedBenefits, setResearchingPerceivedBenefits] = useState(false);
  const [researchingAttributeMappings, setResearchingAttributeMappings] = useState(false);
  const [forceRegenerateIntelligence, setForceRegenerateIntelligence] = useState(false);
  const [forceRegenerate, setForceRegenerate] = useState(false);
  const [forceRegeneratePerceivedBenefits, setForceRegeneratePerceivedBenefits] = useState(false);
  const [forceRegenerateAttributeMappings, setForceRegenerateAttributeMappings] = useState(false);

  if (!category) return null;

  const handleRefreshAll = async () => {
    setRefreshing(true);
    await onRefresh(category.categoryPath);
    setRefreshing(false);
  };

  const handleRefreshAspect = async (aspect: string) => {
    setRefreshingAspect(aspect);
    await onRefreshAspect(category.categoryPath, aspect);
    setRefreshingAspect(null);
  };

  const handleRegenerateAttributes = async () => {
    setRegeneratingAttrs(true);
    try {
      await onRegenerateAttributes(category.id, forceRegenerate);
    } finally {
      setRegeneratingAttrs(false);
    }
  };

  const handleResearchIntelligence = async (depth: 'quick' | 'standard' | 'deep' = 'standard') => {
    setResearchingIntelligence(true);
    try {
      await onResearchIntelligence(category.categoryPath, depth, forceRegenerateIntelligence);
      setForceRegenerateIntelligence(false); // Reset after use
    } finally {
      setResearchingIntelligence(false);
    }
  };

  const handleResearchPerceivedBenefits = async () => {
    setResearchingPerceivedBenefits(true);
    try {
      const categoryName = category.categoryPath.split(' > ').pop() || category.categoryPath;
      await onResearchPerceivedBenefits(categoryName, forceRegeneratePerceivedBenefits);
      setForceRegeneratePerceivedBenefits(false);
    } finally {
      setResearchingPerceivedBenefits(false);
    }
  };

  const handleResearchAttributeMappings = async () => {
    setResearchingAttributeMappings(true);
    try {
      await onResearchAttributeMappings(forceRegenerateAttributeMappings);
      setForceRegenerateAttributeMappings(false);
    } finally {
      setResearchingAttributeMappings(false);
    }
  };

  // Format days old - handle negative values and show "Today" for 0
  const formatDaysOld = (daysOld: number | null) => {
    if (daysOld === null) return '-';
    if (daysOld <= 0) return 'Today';
    return `${daysOld}d`;
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Layers className="h-5 w-5" />
            {category.categoryName}
          </DialogTitle>
          <DialogDescription className="flex items-center justify-between">
            <span className="text-xs font-mono">{category.categoryPath}</span>
            <FreshnessIndicator freshness={category.freshness} />
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="h-[calc(90vh-120px)] pr-4 -mr-4">
          <div className="space-y-6 py-4 pr-4">
            {/* Summary Stats */}
            <div className="grid grid-cols-4 gap-4">
              <Card>
                <CardContent className="pt-4">
                  <div className="text-2xl font-bold">{category.timesUsed}</div>
                  <div className="text-xs text-muted-foreground">Times Used</div>
                </CardContent>
              </Card>
{/* Cost tracking hidden until properly debugged
              <Card>
                <CardContent className="pt-4">
                  <div className="text-2xl font-bold">${category.researchCostUsd.toFixed(4)}</div>
                  <div className="text-xs text-muted-foreground">Research Cost</div>
                </CardContent>
              </Card>
*/}
              <Card>
                <CardContent className="pt-4">
                  <div className="text-2xl font-bold">{category.researchModel || '-'}</div>
                  <div className="text-xs text-muted-foreground">Model</div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-4">
                  <div className="text-2xl font-bold">
                    {formatDaysOld(category.freshness.daysOld)}
                  </div>
                  <div className="text-xs text-muted-foreground">Days Old</div>
                </CardContent>
              </Card>
            </div>

            {/* Research Aspects */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold">Research Aspects</h3>
                <div className="flex gap-2">
                  {onOpenChatDialog && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={onOpenChatDialog}
                    >
                      <MessageSquare className="h-4 w-4 mr-2" />
                      Talk to Knowledge
                    </Button>
                  )}
                  <Button
                    size="sm"
                    onClick={handleRefreshAll}
                    disabled={refreshing || category.status === 'researching'}
                  >
                    {refreshing ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Re-Researching...
                      </>
                    ) : (
                      <>
                        <RefreshCw className="h-4 w-4 mr-2" />
                        Re-Research All
                      </>
                    )}
                  </Button>
                </div>
              </div>

              <div className="space-y-3">
                {(Object.keys(ASPECT_CONFIG) as Array<keyof typeof ASPECT_CONFIG>).map((aspect) => (
                  <div key={aspect} className="flex items-center gap-2">
                    <div className="flex-1">
                      <AspectStatus aspect={aspect} data={category.aspects[aspect]} />
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleRefreshAspect(aspect)}
                      disabled={refreshingAspect === aspect || refreshing}
                      className="shrink-0"
                    >
                      {refreshingAspect === aspect ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <RefreshCw className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                ))}
              </div>
            </div>

            {/* Technical Research Status */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold flex items-center gap-2">
                  <Settings className="h-4 w-4" />
                  Technical Research
                </h3>
              </div>

              <div className="grid grid-cols-5 gap-4 mb-4">
                <Card
                  className={cn(
                    'border cursor-pointer transition-all hover:shadow-md',
                    technicalResearch?.research.canonicalAttributes.hasData
                      ? 'border-green-200 bg-green-50 dark:bg-green-950/20 hover:border-green-400'
                      : 'border-gray-200 bg-gray-50 dark:bg-gray-950/20 hover:border-gray-400'
                  )}
                  onClick={onOpenCanonicalDialog}
                >
                  <CardContent className="pt-4">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <Database className={cn(
                          'h-4 w-4',
                          technicalResearch?.research.canonicalAttributes.hasData ? 'text-green-600 dark:text-green-400' : 'text-gray-400'
                        )} />
                        <span className="font-medium text-sm">Canonical Attributes</span>
                      </div>
                      <Edit3 className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                    <div className="text-2xl font-bold">
                      {technicalResearch?.research.canonicalAttributes.count ?? '-'}
                    </div>
                    <div className="text-xs text-muted-foreground">Technical specs defined</div>
                  </CardContent>
                </Card>

                <Card className={cn(
                  'border',
                  technicalResearch?.research.attributeMappings.hasData
                    ? 'border-green-200 bg-green-50 dark:bg-green-950/20'
                    : 'border-gray-200 bg-gray-50 dark:bg-gray-950/20'
                )}>
                  <CardContent className="pt-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Link2 className={cn(
                        'h-4 w-4',
                        technicalResearch?.research.attributeMappings.hasData ? 'text-green-600 dark:text-green-400' : 'text-gray-400'
                      )} />
                      <span className="font-medium text-sm">Attribute Mappings</span>
                    </div>
                    <div className="text-2xl font-bold">
                      {technicalResearch?.research.attributeMappings.count ?? '-'}
                    </div>
                    <div className="text-xs text-muted-foreground">Tech → benefit mappings</div>
                  </CardContent>
                </Card>

                <Card className={cn(
                  'border',
                  technicalResearch?.research.perceivedBenefits.hasData
                    ? 'border-green-200 bg-green-50 dark:bg-green-950/20'
                    : 'border-gray-200 bg-gray-50 dark:bg-gray-950/20'
                )}>
                  <CardContent className="pt-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Heart className={cn(
                        'h-4 w-4',
                        technicalResearch?.research.perceivedBenefits.hasData ? 'text-green-600 dark:text-green-400' : 'text-gray-400'
                      )} />
                      <span className="font-medium text-sm">Perceived Benefits</span>
                    </div>
                    <div className="text-2xl font-bold">
                      {technicalResearch?.research.perceivedBenefits.count ?? '-'}
                    </div>
                    <div className="text-xs text-muted-foreground">Validated benefits</div>
                  </CardContent>
                </Card>

                <Card
                  className={cn(
                    'border cursor-pointer transition-all hover:shadow-md',
                    technicalResearch?.research.attributeIntelligence?.hasData
                      ? 'border-green-200 bg-green-50 dark:bg-green-950/20 hover:border-green-400'
                      : 'border-gray-200 bg-gray-50 dark:bg-gray-950/20 hover:border-gray-400'
                  )}
                  onClick={onOpenIntelligenceDialog}
                >
                  <CardContent className="pt-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Lightbulb className={cn(
                        'h-4 w-4',
                        technicalResearch?.research.attributeIntelligence?.hasData ? 'text-green-600 dark:text-green-400' : 'text-gray-400'
                      )} />
                      <span className="font-medium text-sm">Attribute Intelligence</span>
                    </div>
                    <div className="text-2xl font-bold">
                      {technicalResearch?.research.attributeIntelligence?.count ?? '-'}
                    </div>
                    <div className="text-xs text-muted-foreground">Value interpretations (click to view)</div>
                  </CardContent>
                </Card>

                <Card
                  className={cn(
                    'border cursor-pointer transition-all hover:shadow-md',
                    technicalResearch?.research.extractionHints?.hasData
                      ? 'border-green-200 bg-green-50 dark:bg-green-950/20 hover:border-green-400'
                      : 'border-gray-200 bg-gray-50 dark:bg-gray-950/20 hover:border-gray-400'
                  )}
                  onClick={onOpenExtractionHintsDialog}
                >
                  <CardContent className="pt-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Wand2 className={cn(
                        'h-4 w-4',
                        technicalResearch?.research.extractionHints?.hasData ? 'text-green-600 dark:text-green-400' : 'text-gray-400'
                      )} />
                      <span className="font-medium text-sm">Extraction Hints</span>
                    </div>
                    <div className="text-2xl font-bold">
                      {technicalResearch?.research.extractionHints?.withHints ?? '-'}
                    </div>
                    <div className="text-xs text-muted-foreground">Field disambiguation rules (click to view)</div>
                  </CardContent>
                </Card>
              </div>

              {/* Regenerate Attributes Section */}
              <Card className="p-4 bg-blue-50 dark:bg-blue-950/20 border-blue-200">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-medium text-sm mb-1">Regenerate Canonical Attributes</h4>
                    <p className="text-xs text-muted-foreground">
                      Generate or regenerate technical attribute definitions for this category using AI
                    </p>
                    <div className="flex items-center gap-2 mt-2">
                      <Checkbox
                        id="forceRegenerate"
                        checked={forceRegenerate}
                        onCheckedChange={(checked) => setForceRegenerate(checked === true)}
                      />
                      <Label htmlFor="forceRegenerate" className="text-xs cursor-pointer">
                        Force regenerate (replace existing attributes)
                      </Label>
                    </div>
                  </div>
                  <Button
                    size="sm"
                    onClick={handleRegenerateAttributes}
                    disabled={regeneratingAttrs}
                    className="shrink-0"
                  >
                    {regeneratingAttrs ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Generating...
                      </>
                    ) : (
                      <>
                        <Zap className="h-4 w-4 mr-2" />
                        {technicalResearch?.research.canonicalAttributes.hasData ? 'Regenerate' : 'Generate'}
                      </>
                    )}
                  </Button>
                </div>
              </Card>

              {/* Research Attribute Intelligence Section */}
              <Card className={cn(
                "p-4 border",
                !technicalResearch?.research.perceivedBenefits.hasData
                  ? "bg-gray-50 dark:bg-gray-950/20 border-gray-200 opacity-75"
                  : "bg-amber-50 dark:bg-amber-950/20 border-amber-200"
              )}>
                <div className="space-y-3">
                  <div>
                    <h4 className="font-medium text-sm mb-1 flex items-center gap-2">
                      Research Attribute Intelligence
                      {!technicalResearch?.research.perceivedBenefits.hasData && (
                        <Badge variant="outline" className="text-xs bg-orange-50 text-orange-700 border-orange-300">
                          Requires: Perceived Benefits
                        </Badge>
                      )}
                    </h4>
                    <p className="text-xs text-muted-foreground">
                      Generate value interpretations (what TPI=60 vs TPI=120 means to customers)
                    </p>
                  </div>
                  {!technicalResearch?.research.perceivedBenefits.hasData ? (
                    <div className="text-xs text-orange-600 dark:text-orange-400 flex items-center gap-1">
                      <AlertTriangle className="h-3 w-3" />
                      Research Perceived Benefits first - intelligence is derived from technical features
                    </div>
                  ) : (
                    <>
                      <div className="flex items-center gap-2 mb-2">
                        <Checkbox
                          id="forceRegenerateIntelligence"
                          checked={forceRegenerateIntelligence}
                          onCheckedChange={(checked) => setForceRegenerateIntelligence(checked === true)}
                        />
                        <Label htmlFor="forceRegenerateIntelligence" className="text-xs text-muted-foreground cursor-pointer">
                          Force regenerate (delete existing and research all)
                        </Label>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          onClick={() => handleResearchIntelligence('quick')}
                          disabled={researchingIntelligence}
                          variant="outline"
                          title="Fast preview with fewer sources"
                        >
                          {researchingIntelligence ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <><Zap className="h-4 w-4 mr-1" />Quick</>
                          )}
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => handleResearchIntelligence('standard')}
                          disabled={researchingIntelligence}
                          variant="default"
                          title="Balanced research with web grounding (recommended)"
                        >
                          {researchingIntelligence ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <><Lightbulb className="h-4 w-4 mr-1" />Standard</>
                          )}
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => handleResearchIntelligence('deep')}
                          disabled={researchingIntelligence}
                          variant="outline"
                          title="Comprehensive research with more sources"
                        >
                          {researchingIntelligence ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <><Search className="h-4 w-4 mr-1" />Comprehensive</>
                          )}
                        </Button>
                      </div>
                    </>
                  )}
                </div>
              </Card>

              {/* Research Extraction Hints Section */}
              <Card className={cn(
                "p-4 border",
                !technicalResearch?.research.canonicalAttributes.hasData
                  ? "bg-gray-50 dark:bg-gray-950/20 border-gray-200 opacity-75"
                  : "bg-purple-50 dark:bg-purple-950/20 border-purple-200"
              )}>
                <div className="space-y-3">
                  <div>
                    <h4 className="font-medium text-sm mb-1 flex items-center gap-2">
                      Research Extraction Hints
                      {!technicalResearch?.research.canonicalAttributes.hasData && (
                        <Badge variant="outline" className="text-xs bg-yellow-50 text-yellow-700 border-yellow-300">
                          Recommended: Canonical Attributes first
                        </Badge>
                      )}
                    </h4>
                    <p className="text-xs text-muted-foreground">
                      Generate field disambiguation rules (which fields to prefer/avoid, qualifier preservation, semantic clarifications)
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      onClick={async () => {
                        setResearchingExtractionHints(true);
                        try {
                          await onResearchExtractionHints(false);
                        } finally {
                          setResearchingExtractionHints(false);
                        }
                      }}
                      disabled={researchingExtractionHints}
                      variant="default"
                      title="Research extraction hints for attributes without them"
                    >
                      {researchingExtractionHints ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Researching...
                        </>
                      ) : (
                        <>
                          <Wand2 className="h-4 w-4 mr-1" />
                          Research Hints
                        </>
                      )}
                    </Button>
                    <Button
                      size="sm"
                      onClick={async () => {
                        setResearchingExtractionHints(true);
                        try {
                          await onResearchExtractionHints(true);
                        } finally {
                          setResearchingExtractionHints(false);
                        }
                      }}
                      disabled={researchingExtractionHints}
                      variant="outline"
                      title="Force regenerate all extraction hints"
                    >
                      <RefreshCw className="h-4 w-4 mr-1" />
                      Force Regenerate
                    </Button>
                  </div>
                </div>
              </Card>

              {/* Research Perceived Benefits Section */}
              <Card className={cn(
                "p-4 border",
                !technicalResearch?.research.canonicalAttributes.hasData
                  ? "bg-gray-50 dark:bg-gray-950/20 border-gray-200 opacity-75"
                  : "bg-pink-50 dark:bg-pink-950/20 border-pink-200"
              )}>
                <div className="space-y-3">
                  <div>
                    <h4 className="font-medium text-sm mb-1 flex items-center gap-2">
                      Research Perceived Benefits
                      {!technicalResearch?.research.canonicalAttributes.hasData && (
                        <Badge variant="outline" className="text-xs bg-yellow-50 text-yellow-700 border-yellow-300">
                          Recommended: Canonical Attributes first
                        </Badge>
                      )}
                    </h4>
                    <p className="text-xs text-muted-foreground">
                      Generate customer-perceived benefits and purchase drivers for this category
                    </p>
                  </div>
                  <div className="flex items-center gap-2 mb-2">
                    <Checkbox
                      id="forceRegeneratePerceivedBenefits"
                      checked={forceRegeneratePerceivedBenefits}
                      onCheckedChange={(checked) => setForceRegeneratePerceivedBenefits(checked === true)}
                    />
                    <Label htmlFor="forceRegeneratePerceivedBenefits" className="text-xs text-muted-foreground cursor-pointer">
                      Force regenerate (delete existing and research all)
                    </Label>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      onClick={handleResearchPerceivedBenefits}
                      disabled={researchingPerceivedBenefits}
                      variant="default"
                      title="Research perceived benefits for this category"
                    >
                      {researchingPerceivedBenefits ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Researching...
                        </>
                      ) : (
                        <>
                          <Heart className="h-4 w-4 mr-1" />
                          Research Benefits
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </Card>

              {/* Research Attribute Mappings Section */}
              <Card className={cn(
                "p-4 border",
                !technicalResearch?.research.perceivedBenefits.hasData
                  ? "bg-gray-50 dark:bg-gray-950/20 border-gray-200 opacity-75"
                  : "bg-cyan-50 dark:bg-cyan-950/20 border-cyan-200"
              )}>
                <div className="space-y-3">
                  <div>
                    <h4 className="font-medium text-sm mb-1 flex items-center gap-2">
                      Research Attribute Mappings
                      {!technicalResearch?.research.perceivedBenefits.hasData && (
                        <Badge variant="outline" className="text-xs bg-orange-50 text-orange-700 border-orange-300">
                          Requires: Perceived Benefits
                        </Badge>
                      )}
                    </h4>
                    <p className="text-xs text-muted-foreground">
                      Generate persona → benefit mappings (which personas care about which benefits)
                    </p>
                  </div>
                  {!technicalResearch?.research.perceivedBenefits.hasData ? (
                    <div className="text-xs text-orange-600 dark:text-orange-400 flex items-center gap-1">
                      <AlertTriangle className="h-3 w-3" />
                      Research Perceived Benefits first to enable this feature
                    </div>
                  ) : (
                    <>
                      <div className="flex items-center gap-2 mb-2">
                        <Checkbox
                          id="forceRegenerateAttributeMappings"
                          checked={forceRegenerateAttributeMappings}
                          onCheckedChange={(checked) => setForceRegenerateAttributeMappings(checked === true)}
                        />
                        <Label htmlFor="forceRegenerateAttributeMappings" className="text-xs text-muted-foreground cursor-pointer">
                          Force regenerate (delete existing and research all)
                        </Label>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          onClick={handleResearchAttributeMappings}
                          disabled={researchingAttributeMappings}
                          variant="default"
                          title="Generate attribute mappings for this sector"
                        >
                          {researchingAttributeMappings ? (
                            <>
                              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                              Generating...
                            </>
                          ) : (
                            <>
                              <Link2 className="h-4 w-4 mr-1" />
                              Generate Mappings
                            </>
                          )}
                        </Button>
                      </div>
                    </>
                  )}
                </div>
              </Card>
            </div>

            {/* Tier Vocabulary Preview */}
            {category.aspects.tier_vocabulary.hasData && (
              <div>
                <h3 className="font-semibold mb-3">Tier Vocabulary Preview</h3>
                <div className="grid grid-cols-2 gap-4">
                  {Object.entries(category.aspects.tier_vocabulary.data || {}).map(([tier, vocab]: [string, any]) => (
                    <Card key={tier} className="p-3">
                      <h4 className="font-medium text-sm capitalize mb-2">{tier.replace('_', ' ')}</h4>
                      <div className="space-y-2">
                        <div>
                          <span className="text-xs text-green-600 dark:text-green-400 font-medium">Use:</span>
                          <div className="flex flex-wrap gap-1 mt-1">
                            {(vocab?.use || []).slice(0, 4).map((term: string, i: number) => (
                              <Badge key={i} variant="outline" className="text-xs bg-green-50 dark:bg-green-950/30 text-green-800 dark:text-green-300">
                                {term}
                              </Badge>
                            ))}
                          </div>
                        </div>
                        <div>
                          <span className="text-xs text-red-600 dark:text-red-400 font-medium">Avoid:</span>
                          <div className="flex flex-wrap gap-1 mt-1">
                            {(vocab?.avoid || []).slice(0, 4).map((term: string, i: number) => (
                              <Badge key={i} variant="outline" className="text-xs bg-red-50 dark:bg-red-950/30 text-red-800 dark:text-red-300">
                                {term}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {/* Error Display */}
            {category.freshness.error && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  <strong>Research Error:</strong> {category.freshness.error}
                </AlertDescription>
              </Alert>
            )}
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}

export default function CategoryResearch() {
  const { selectedSite } = useSite();
  const { user } = useAuth();
  const { toast } = useToast();

  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState<CategoryCharacteristics[]>([]);
  const [availableCategories, setAvailableCategories] = useState<AvailableCategory[]>([]);
  const [summary, setSummary] = useState<SummaryStats | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<CategoryCharacteristics | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [viewMode, setViewMode] = useState<'catalog' | 'gpt-browser' | 'researched'>('catalog');
  const [triggeringResearch, setTriggeringResearch] = useState<string | null>(null);

  // Independent sector selection (removes site/product dependency)
  const [sectors, setSectors] = useState<Sector[]>([]);
  const [selectedSectorId, setSelectedSectorId] = useState<number | null>(null);
  const [newCategoryName, setNewCategoryName] = useState<string>('');

  // Resolved sector for write operations (ensures research goes to correct sector)
  const [resolvedCategorySectorId, setResolvedCategorySectorId] = useState<number | null>(null);
  const [sectorMismatchWarning, setSectorMismatchWarning] = useState<string | null>(null);
  const [technicalResearch, setTechnicalResearch] = useState<TechnicalResearchStatus | null>(null);

  // Research dimension metadata (from backend)
  const [dimensionNames, setDimensionNames] = useState<string[]>([]);
  const [dimensionShortNames, setDimensionShortNames] = useState<string[]>([]);
  const [dimensionToStep, setDimensionToStep] = useState<string[]>([]);
  const [enrichmentRelevantDimensions, setEnrichmentRelevantDimensions] = useState<number[]>([]);

  // GPT Taxonomy browser state
  // GPT Browser is now shown in its own tab (removed showGptBrowser state)
  const [gptTaxonomy, setGptTaxonomy] = useState<any[]>([]);
  const [gptBreadcrumb, setGptBreadcrumb] = useState<any[]>([]);
  const [gptLoading, setGptLoading] = useState(false);

  // Canonical attributes dialog state
  const [canonicalDialogOpen, setCanonicalDialogOpen] = useState(false);
  const [canonicalAttributes, setCanonicalAttributes] = useState<CanonicalAttribute[]>([]);
  const [canonicalLoading, setCanonicalLoading] = useState(false);

  // Attribute intelligence dialog state
  const [intelligenceDialogOpen, setIntelligenceDialogOpen] = useState(false);
  const [attributeIntelligence, setAttributeIntelligence] = useState<AttributeIntelligenceEntry[]>([]);
  const [intelligenceLoading, setIntelligenceLoading] = useState(false);

  // Extraction hints dialog state
  const [extractionHintsDialogOpen, setExtractionHintsDialogOpen] = useState(false);
  const [extractionHints, setExtractionHints] = useState<ExtractionHint[]>([]);
  const [extractionHintsLoading, setExtractionHintsLoading] = useState(false);
  const [researchingExtractionHints, setResearchingExtractionHints] = useState(false);
  const [editingIntelligence, setEditingIntelligence] = useState<AttributeIntelligenceEntry | null>(null);
  const [savingIntelligence, setSavingIntelligence] = useState(false);
  const [editingAttribute, setEditingAttribute] = useState<CanonicalAttribute | null>(null);
  const [savingAttribute, setSavingAttribute] = useState(false);
  const [deletingAttributeId, setDeletingAttributeId] = useState<string | null>(null);
  const [canonicalSearch, setCanonicalSearch] = useState('');
  const [showNewAttributeForm, setShowNewAttributeForm] = useState(false);
  const [newAttribute, setNewAttribute] = useState<Partial<CanonicalAttribute>>({
    data_type: 'string',
    enum_values: [],
  });
  const [newEnumValue, setNewEnumValue] = useState('');

  // AI suggestion state
  const [aiSuggestions, setAiSuggestions] = useState<{
    unit?: string | null;
    typical_min?: number | null;
    typical_max?: number | null;
    description?: string | null;
    enum_values?: string[] | null;
    reasoning?: string | null;
    sample_values_found?: string[];
    based_on_research?: boolean;
    research_title?: string | null;
  } | null>(null);
  const [loadingAiSuggestions, setLoadingAiSuggestions] = useState(false);

  // Batch research state
  const [batchResearchDialogOpen, setBatchResearchDialogOpen] = useState(false);
  const [batchResearchJob, setBatchResearchJob] = useState<{
    id: string;
    status: string;
    totalCategories: number;
    completedCategories: number;
    failedCategories: number;
    currentStep: string | null;
    progressPercent: number;
    progressDetails: Record<string, { steps: Record<string, string>; error?: string }>;
    lastError: string | null;
  } | null>(null);
  const [batchResearchPolling, setBatchResearchPolling] = useState(false);
  const [selectedBatchCategories, setSelectedBatchCategories] = useState<string[]>([]);
  const [batchResearchSteps, setBatchResearchSteps] = useState<string[]>([
    'characteristics', 'perceived_benefits', 'canonical', 'intelligence', 'extraction_hints'
  ]);
  const [startingBatchResearch, setStartingBatchResearch] = useState(false);

  // Knowledge chat state
  const [chatDialogOpen, setChatDialogOpen] = useState(false);
  const [chatMessage, setChatMessage] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [chatParsedIntent, setChatParsedIntent] = useState<{
    operation: string;
    conceptType: string;
    conceptName: string;
    context: string;
    confirmationMessage: string;
  } | null>(null);
  const [chatExecuting, setChatExecuting] = useState(false);
  const [chatHistory, setChatHistory] = useState<Array<{
    role: 'user' | 'assistant';
    content: string;
    result?: any;
  }>>([]);

  const isSuperAdmin = user?.role === 'super_admin';

  // Get sector ID - allow manual selection to override site's sector
  const siteSectorId = selectedSite?.sector_id || selectedSite?.sector_ids?.[0];
  const sectorId = selectedSectorId || siteSectorId;

  // Redirect if not super admin
  useEffect(() => {
    if (user && !isSuperAdmin) {
      toast({
        title: 'Access Denied',
        description: 'This page is only available to Super Admins',
        variant: 'destructive',
      });
    }
  }, [user, isSuperAdmin, toast]);

  // Load all sectors for the dropdown (allows selecting any sector)
  useEffect(() => {
    const loadSectors = async () => {
      try {
        const res = await authFetch(`${API_URL}/sectors`);
        if (res.ok) {
          const data = await res.json();
          setSectors(Array.isArray(data) ? data : (Array.isArray(data.sectors) ? data.sectors : []));
        }
      } catch (error) {
        console.error('Error loading sectors:', error);
      }
    };
    loadSectors();
  }, []);

  const loadData = useCallback(async () => {
    if (!sectorId) return;

    setLoading(true);
    try {
      // Load categories, summary, and available categories in parallel (sector-level data)
      const siteIdParam = selectedSite?.id ? `?siteId=${selectedSite.id}` : '';
      const [categoriesRes, summaryRes, availableRes] = await Promise.all([
        authFetch(`${API_URL}/category-characteristics/${sectorId}${siteIdParam}`),
        authFetch(`${API_URL}/category-characteristics/${sectorId}/summary`),
        authFetch(`${API_URL}/category-characteristics/${sectorId}/available-categories`),
      ]);

      if (categoriesRes.ok) {
        const data = await categoriesRes.json();
        setCategories(data.data || []);
        if (data.dimensionNames) setDimensionNames(data.dimensionNames);
        if (data.dimensionShortNames) setDimensionShortNames(data.dimensionShortNames);
        if (data.dimensionToStep) setDimensionToStep(data.dimensionToStep);
        if (data.enrichmentRelevantDimensions) setEnrichmentRelevantDimensions(data.enrichmentRelevantDimensions);
      }

      if (summaryRes.ok) {
        const data = await summaryRes.json();
        setSummary(data.data || null);
      }

      if (availableRes.ok) {
        const data = await availableRes.json();
        setAvailableCategories(data.data || []);
      }
    } catch (error) {
      console.error('Error loading category characteristics:', error);
      toast({
        title: 'Error',
        description: 'Failed to load category characteristics',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  }, [sectorId, selectedSite?.id, toast]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Load technical research status when a category is selected
  useEffect(() => {
    const loadTechnicalResearch = async () => {
      if (!selectedCategory || !sectorId) {
        setTechnicalResearch(null);
        return;
      }

      try {
        const response = await authFetch(
          `${API_URL}/sectors/${sectorId}/categories/${selectedCategory.id}/research-status`
        );
        if (response.ok) {
          const data = await response.json();
          setTechnicalResearch(data.data || null);
        }
      } catch (error) {
        console.error('Error loading technical research status:', error);
      }
    };

    loadTechnicalResearch();
  }, [selectedCategory, sectorId]);

  // Resolve the correct sector for the selected category
  // This ensures write operations (chat, research) go to the correct sector
  useEffect(() => {
    const resolveCategorySector = async () => {
      if (!selectedCategory || !selectedSite?.sector_ids || selectedSite.sector_ids.length <= 1) {
        // Single sector or no category - use current sector
        setResolvedCategorySectorId(sectorId || null);
        setSectorMismatchWarning(null);
        return;
      }

      try {
        // Extract leaf category name from path
        const categoryPath = selectedCategory.categoryPath;
        const leafCategory = categoryPath.split('>').pop()?.trim() || categoryPath;

        const response = await authFetch(
          `${API_URL}/category-characteristics/resolve-sector?sectorIds=${selectedSite.sector_ids.join(',')}&categoryName=${encodeURIComponent(leafCategory)}`
        );
        const data = await response.json();

        if (data.success && data.sectorId) {
          setResolvedCategorySectorId(data.sectorId);

          // Check if there's a mismatch
          if (data.sectorId !== sectorId) {
            const correctSector = sectors.find(s => s.id === data.sectorId);
            const currentSector = sectors.find(s => s.id.toString() === sectorId?.toString());
            setSectorMismatchWarning(
              `This category belongs to "${correctSector?.name || `Sector ${data.sectorId}`}" but you're viewing "${currentSector?.name || `Sector ${sectorId}`}". Write operations will use the correct sector.`
            );
          } else {
            setSectorMismatchWarning(null);
          }
        } else {
          setResolvedCategorySectorId(sectorId || null);
          setSectorMismatchWarning(null);
        }
      } catch (error) {
        console.error('Error resolving category sector:', error);
        setResolvedCategorySectorId(sectorId || null);
        setSectorMismatchWarning(null);
      }
    };

    resolveCategorySector();
  }, [selectedCategory, selectedSite?.sector_ids, sectorId, sectors]);

  // Helper to get the sector ID for write operations
  const getWriteSectorId = useCallback(() => {
    return resolvedCategorySectorId || sectorId;
  }, [resolvedCategorySectorId, sectorId]);

  const handleRefresh = async (categoryPath: string) => {
    const writeSectorId = getWriteSectorId();
    if (!writeSectorId) return;

    try {
      const response = await authFetch(
        `${API_URL}/category-characteristics/${writeSectorId}/category/${encodeURIComponent(categoryPath)}/refresh`,
        { method: 'POST' }
      );

      if (response.ok) {
        toast({
          title: 'Research Complete',
          description: 'Category characteristics have been refreshed',
        });
        await loadData();
        // Reload selected category if open
        if (selectedCategory?.categoryPath === categoryPath) {
          const updated = categories.find(c => c.categoryPath === categoryPath);
          if (updated) setSelectedCategory(updated);
        }
      } else {
        const error = await response.json();
        throw new Error(error.error);
      }
    } catch (error: any) {
      toast({
        title: 'Research Failed',
        description: error.message,
        variant: 'destructive',
      });
    }
  };

  const handleRefreshAspect = async (categoryPath: string, aspect: string) => {
    const writeSectorId = getWriteSectorId();
    if (!writeSectorId) return;

    try {
      const response = await authFetch(
        `${API_URL}/category-characteristics/${writeSectorId}/category/${encodeURIComponent(categoryPath)}/refresh/${aspect}`,
        { method: 'POST' }
      );

      if (response.ok) {
        toast({
          title: 'Aspect Refreshed',
          description: `${ASPECT_CONFIG[aspect as keyof typeof ASPECT_CONFIG]?.label || aspect} has been updated`,
        });
        await loadData();
        // Reload selected category if open
        if (selectedCategory?.categoryPath === categoryPath) {
          const updated = categories.find((c) => c.categoryPath === categoryPath);
          if (updated) setSelectedCategory(updated);
        }
      } else {
        const error = await response.json();
        throw new Error(error.error);
      }
    } catch (error: any) {
      toast({
        title: 'Refresh Failed',
        description: error.message,
        variant: 'destructive',
      });
    }
  };

  const handleMarkAllStale = async () => {
    if (!sectorId) return;

    try {
      const response = await authFetch(
        `${API_URL}/category-characteristics/${sectorId}/mark-stale`,
        { method: 'POST' }
      );

      if (response.ok) {
        const data = await response.json();
        toast({
          title: 'Categories Marked Stale',
          description: `${data.markedCategories?.length || 0} categories will be refreshed on next use`,
        });
        await loadData();
      }
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    }
  };

  // Research a new category by name (no product dependency required)
  const handleResearchNewCategory = async () => {
    if (!sectorId || !newCategoryName.trim()) return;

    const categoryPath = newCategoryName.trim();
    setTriggeringResearch(categoryPath);

    try {
      const response = await authFetch(
        `${API_URL}/category-characteristics/${sectorId}/category/${encodeURIComponent(categoryPath)}/refresh`,
        { method: 'POST' }
      );

      if (response.ok) {
        toast({
          title: 'Research Complete',
          description: `Category "${categoryPath}" has been researched`,
        });
        setNewCategoryName('');
        await loadData();
      } else {
        const error = await response.json();
        throw new Error(error.error || 'Failed to research category');
      }
    } catch (error: any) {
      toast({
        title: 'Research Failed',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setTriggeringResearch(null);
    }
  };

  const handleTriggerResearch = async (categoryPath: string) => {
    if (!sectorId) return;

    setTriggeringResearch(categoryPath);
    const categoryName = categoryPath.split(' > ').pop() || categoryPath;

    try {
      // Resolve the correct sector for this category
      let targetSectorId = sectorId;
      if (selectedSite?.sector_ids && selectedSite.sector_ids.length > 1) {
        const leafCategory = categoryPath.split('>').pop()?.trim() || categoryPath;
        try {
          const resolveResponse = await authFetch(
            `${API_URL}/category-characteristics/resolve-sector?sectorIds=${selectedSite.sector_ids.join(',')}&categoryName=${encodeURIComponent(leafCategory)}`
          );
          const resolveData = await resolveResponse.json();
          if (resolveData.success && resolveData.sectorId) {
            targetSectorId = resolveData.sectorId;
            if (targetSectorId !== sectorId) {
              const targetSectorName = sectors.find(s => s.id.toString() === targetSectorId.toString())?.name || `Sector ${targetSectorId}`;
              console.log(`[CategoryResearch] Resolved "${leafCategory}" to sector ${targetSectorId} (${targetSectorName})`);
            }
          }
        } catch (e) {
          console.warn('[CategoryResearch] Failed to resolve sector, using current:', e);
        }
      }

      // Show initial toast - research can take 1-2 minutes
      toast({
        title: 'Research Started',
        description: `Researching ${categoryName}... This may take 1-2 minutes.`,
      });

      // Fire the research request - don't wait for completion (may timeout)
      // Use AbortController to handle timeout gracefully
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000); // 10s timeout for initial response

      try {
        const response = await authFetch(
          `${API_URL}/category-characteristics/${targetSectorId}/category/${encodeURIComponent(categoryPath)}/refresh`,
          { method: 'POST', signal: controller.signal }
        );
        clearTimeout(timeoutId);

        if (response.ok) {
          // Research completed within timeout
          toast({
            title: 'Research Complete',
            description: `${categoryName} research finished successfully.`,
          });
          await loadData();
          setTriggeringResearch(null);
          return;
        } else {
          const error = await response.json();
          throw new Error(error.error);
        }
      } catch (fetchError: any) {
        clearTimeout(timeoutId);

        // If aborted or network error, start polling
        if (fetchError.name === 'AbortError' || fetchError.message?.includes('fetch')) {
          console.log('[CategoryResearch] Request timeout, starting status polling...');
          // Continue to polling below
        } else {
          throw fetchError;
        }
      }

      // Poll for research completion (handles timeout/long-running research)
      const maxPolls = 24; // 2 minutes total (24 * 5s)
      const pollInterval = 5000; // 5 seconds

      for (let i = 0; i < maxPolls; i++) {
        await new Promise(resolve => setTimeout(resolve, pollInterval));

        try {
          const statusResponse = await authFetch(
            `${API_URL}/category-characteristics/${targetSectorId}/category/${encodeURIComponent(categoryPath)}`
          );

          if (statusResponse.ok) {
            const data = await statusResponse.json();
            const status = data.freshness?.status || data.status;

            if (status === 'active' || status === 'fresh') {
              toast({
                title: 'Research Complete',
                description: `${categoryName} research finished successfully.`,
              });
              await loadData();
              setTriggeringResearch(null);
              return;
            } else if (status === 'failed') {
              throw new Error(data.freshness?.error || 'Research failed');
            }
            // If 'researching' or 'pending', continue polling
            console.log(`[CategoryResearch] Status: ${status}, continuing to poll...`);
          }
        } catch (pollError) {
          console.warn('[CategoryResearch] Poll error:', pollError);
          // Continue polling on error
        }
      }

      // Timeout after max polls
      toast({
        title: 'Research In Progress',
        description: `${categoryName} research is taking longer than expected. Refresh the page to check status.`,
        variant: 'default',
      });

    } catch (error: any) {
      toast({
        title: 'Research Failed',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setTriggeringResearch(null);
    }
  };

  // GPT Taxonomy browser functions
  const loadGptTaxonomy = async (parentId?: number) => {
    if (!sectorId) return;

    setGptLoading(true);
    try {
      const params = new URLSearchParams();
      if (parentId) params.append('parentId', String(parentId));

      const response = await authFetch(
        `${API_URL}/sectors/${sectorId}/taxonomy${params.toString() ? '?' + params.toString() : ''}`
      );
      if (response.ok) {
        const data = await response.json();
        setGptTaxonomy(data.taxonomy || []);
      }
    } catch (error) {
      console.error('Error loading GPT taxonomy:', error);
      toast({
        title: 'Error',
        description: 'Failed to load category taxonomy',
        variant: 'destructive',
      });
    } finally {
      setGptLoading(false);
    }
  };

  const handleGptCategoryClick = async (category: any) => {
    // Navigate into this category
    setGptBreadcrumb([...gptBreadcrumb, category]);
    await loadGptTaxonomy(category.id);
  };

  const handleGptBreadcrumbClick = async (index: number) => {
    if (index === -1) {
      // Root level
      setGptBreadcrumb([]);
      await loadGptTaxonomy();
    } else {
      const targetCategory = gptBreadcrumb[index];
      setGptBreadcrumb(gptBreadcrumb.slice(0, index + 1));
      await loadGptTaxonomy(targetCategory.id);
    }
  };

  const handleResearchGptCategory = async (category: any) => {
    // Use the full category path from the taxonomy entry (already includes full GPT path)
    const fullPath = category.category_path;

    // Trigger research using the existing function
    await handleTriggerResearch(fullPath);
  };

  const handleRegenerateAttributes = async (categoryId: string, forceRegenerate: boolean) => {
    if (!sectorId) return;

    try {
      const response = await authFetch(
        `${API_URL}/sectors/${sectorId}/categories/${categoryId}/research-attributes`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ forceRegenerate }),
        }
      );

      if (response.ok) {
        const data = await response.json();
        toast({
          title: 'Attributes Generated',
          description: data.message || 'Canonical attributes generated successfully',
        });
        // Reload technical research status
        const statusResponse = await authFetch(
          `${API_URL}/sectors/${sectorId}/categories/${categoryId}/research-status`
        );
        if (statusResponse.ok) {
          const statusData = await statusResponse.json();
          setTechnicalResearch(statusData.data || null);
        }
      } else {
        const error = await response.json();
        throw new Error(error.error || 'Failed to generate attributes');
      }
    } catch (error: any) {
      toast({
        title: 'Generation Failed',
        description: error.message,
        variant: 'destructive',
      });
    }
  };

  const handleResearchIntelligence = async (categoryPath: string, depth: 'quick' | 'standard' | 'deep' = 'standard', forceRegenerate: boolean = false) => {
    if (!sectorId || !selectedSite?.id) return;

    // Extract just the category name from the path
    const categoryName = categoryPath.split(' > ').pop() || categoryPath;

    try {
      const response = await authFetch(
        `${API_URL}/category-research/${sectorId}/attribute-intelligence`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ categoryName, siteId: selectedSite.id, depth, forceRegenerate }),
        }
      );

      if (response.ok) {
        const data = await response.json();
        toast({
          title: 'Intelligence Generated',
          description: data.message || `Generated ${data.attributesResearched || 0} attribute intelligence entries`,
        });
        // Reload technical research status to update the count
        if (selectedCategory) {
          const statusResponse = await authFetch(
            `${API_URL}/sectors/${sectorId}/categories/${selectedCategory.id}/research-status`
          );
          if (statusResponse.ok) {
            const statusData = await statusResponse.json();
            setTechnicalResearch(statusData.data || null);
          }
        }
      } else {
        const error = await response.json();
        throw new Error(error.error || 'Failed to research attribute intelligence');
      }
    } catch (error: any) {
      toast({
        title: 'Research Failed',
        description: error.message,
        variant: 'destructive',
      });
    }
  };

  // Load extraction hints for the dialog
  const loadExtractionHints = async () => {
    if (!sectorId || !selectedCategory?.id) return;

    setExtractionHintsLoading(true);
    try {
      const response = await authFetch(
        `${API_URL}/sectors/${sectorId}/categories/${selectedCategory.id}/extraction-hints`
      );
      if (response.ok) {
        const data = await response.json();
        setExtractionHints(data.data?.hints || []);
      }
    } catch (error) {
      console.error('Error loading extraction hints:', error);
      toast({
        title: 'Error',
        description: 'Failed to load extraction hints',
        variant: 'destructive',
      });
    } finally {
      setExtractionHintsLoading(false);
    }
  };

  // Open extraction hints dialog
  const handleOpenExtractionHintsDialog = async () => {
    setExtractionHintsDialogOpen(true);
    await loadExtractionHints();
  };

  // Research extraction hints
  const handleResearchExtractionHints = async (forceRegenerate: boolean = false) => {
    if (!sectorId || !selectedCategory?.id) return;

    setResearchingExtractionHints(true);
    try {
      const response = await authFetch(
        `${API_URL}/sectors/${sectorId}/categories/${selectedCategory.id}/research-extraction-hints`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            siteId: selectedSite?.id,
            forceRegenerate
          }),
        }
      );

      if (response.ok) {
        const data = await response.json();
        toast({
          title: 'Extraction Hints Researched',
          description: data.message || `Researched ${data.data?.specsResearched || 0} specifications`,
        });
        // Reload technical research status and hints
        if (selectedCategory) {
          const statusResponse = await authFetch(
            `${API_URL}/sectors/${sectorId}/categories/${selectedCategory.id}/research-status`
          );
          if (statusResponse.ok) {
            const statusData = await statusResponse.json();
            setTechnicalResearch(statusData.data || null);
          }
        }
        // Reload hints if dialog is open
        if (extractionHintsDialogOpen) {
          await loadExtractionHints();
        }
      } else {
        const error = await response.json();
        throw new Error(error.error || 'Failed to research extraction hints');
      }
    } catch (error: any) {
      toast({
        title: 'Research Failed',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setResearchingExtractionHints(false);
    }
  };

  const handleResearchPerceivedBenefits = async (categoryName: string, forceRegenerate: boolean = false) => {
    if (!sectorId) return;

    try {
      const response = await authFetch(
        `${API_URL}/category-research/${sectorId}/research`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ categoryName, forceRegenerate }),
        }
      );

      if (response.ok) {
        const data = await response.json();
        toast({
          title: 'Perceived Benefits Researched',
          description: data.message || `Research completed for ${categoryName}`,
        });
        // Reload technical research status
        if (selectedCategory) {
          const statusResponse = await authFetch(
            `${API_URL}/sectors/${sectorId}/categories/${selectedCategory.id}/research-status`
          );
          if (statusResponse.ok) {
            const statusData = await statusResponse.json();
            setTechnicalResearch(statusData.data || null);
          }
        }
      } else {
        const error = await response.json();
        throw new Error(error.error || 'Failed to research perceived benefits');
      }
    } catch (error: any) {
      toast({
        title: 'Research Failed',
        description: error.message,
        variant: 'destructive',
      });
    }
  };

  const handleResearchAttributeMappings = async (forceRegenerate: boolean = false) => {
    if (!sectorId) return;

    try {
      const response = await authFetch(
        `${API_URL}/mappings/populate/${sectorId}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            skipBenefitAttribute: false,
            skipValueRanges: true,
            skipPersonaBenefit: false,
            skipAttributeCategory: true,
            forceRegenerate
          }),
        }
      );

      if (response.ok) {
        const data = await response.json();
        toast({
          title: 'Attribute Mappings Generated',
          description: data.message || `Generated persona-benefit mappings for sector ${sectorId}`,
        });
        // Reload technical research status
        if (selectedCategory) {
          const statusResponse = await authFetch(
            `${API_URL}/sectors/${sectorId}/categories/${selectedCategory.id}/research-status`
          );
          if (statusResponse.ok) {
            const statusData = await statusResponse.json();
            setTechnicalResearch(statusData.data || null);
          }
        }
      } else {
        const error = await response.json();
        throw new Error(error.error || 'Failed to generate attribute mappings');
      }
    } catch (error: any) {
      toast({
        title: 'Generation Failed',
        description: error.message,
        variant: 'destructive',
      });
    }
  };

  // Load canonical attributes for the dialog
  const loadCanonicalAttributes = async () => {
    if (!sectorId || !technicalResearch?.categoryPath) return;

    setCanonicalLoading(true);
    try {
      const response = await authFetch(
        `${API_URL}/sectors/${sectorId}/canonical-attributes?category=${encodeURIComponent(technicalResearch.categoryPath)}`
      );
      if (response.ok) {
        const data = await response.json();
        setCanonicalAttributes(data.data?.attributes || []);
      }
    } catch (error) {
      console.error('Error loading canonical attributes:', error);
      toast({
        title: 'Error',
        description: 'Failed to load canonical attributes',
        variant: 'destructive',
      });
    } finally {
      setCanonicalLoading(false);
    }
  };

  // Open canonical attributes dialog
  const handleOpenCanonicalDialog = async () => {
    setCanonicalDialogOpen(true);
    await loadCanonicalAttributes();
  };

  // Load attribute intelligence for the dialog
  const loadAttributeIntelligence = async () => {
    if (!selectedSite?.id || !technicalResearch?.categoryName) return;

    setIntelligenceLoading(true);
    try {
      const response = await authFetch(
        `${API_URL}/category-research/${sectorId}/attribute-intelligence/${selectedSite.id}?categoryName=${encodeURIComponent(technicalResearch.categoryName)}`
      );
      if (response.ok) {
        const data = await response.json();
        setAttributeIntelligence(data.intelligence || []);
      }
    } catch (error) {
      console.error('Error loading attribute intelligence:', error);
      toast({
        title: 'Error',
        description: 'Failed to load attribute intelligence',
        variant: 'destructive',
      });
    } finally {
      setIntelligenceLoading(false);
    }
  };

  // Open attribute intelligence dialog
  const handleOpenIntelligenceDialog = async () => {
    setIntelligenceDialogOpen(true);
    await loadAttributeIntelligence();
  };

  // Save edited attribute intelligence
  const handleSaveIntelligence = async () => {
    if (!editingIntelligence) return;

    setSavingIntelligence(true);
    try {
      const response = await authFetch(
        `/api/domain-knowledge/${editingIntelligence.id}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            research_findings: editingIntelligence.research_findings,
            knowledge_text: editingIntelligence.knowledge_text,
          }),
        }
      );

      if (!response.ok) {
        throw new Error('Failed to save');
      }

      // Update local state
      setAttributeIntelligence(prev =>
        prev.map(intel =>
          intel.id === editingIntelligence.id ? editingIntelligence : intel
        )
      );

      setEditingIntelligence(null);
      toast({
        title: 'Saved',
        description: 'Attribute intelligence updated successfully',
      });
    } catch (error) {
      console.error('Error saving intelligence:', error);
      toast({
        title: 'Error',
        description: 'Failed to save attribute intelligence',
        variant: 'destructive',
      });
    } finally {
      setSavingIntelligence(false);
    }
  };

  // Save edited attribute
  const handleSaveAttribute = async () => {
    if (!sectorId || !editingAttribute) return;

    setSavingAttribute(true);
    try {
      const response = await authFetch(
        `${API_URL}/sectors/${sectorId}/canonical-attributes/${editingAttribute.id}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            attribute_name: editingAttribute.attribute_name,
            canonical_name: editingAttribute.canonical_name,
            data_type: editingAttribute.data_type,
            unit: editingAttribute.unit,
            typical_min: editingAttribute.typical_min,
            typical_max: editingAttribute.typical_max,
            enum_values: editingAttribute.enum_values,
            description: editingAttribute.description,
          }),
        }
      );

      if (response.ok) {
        const data = await response.json();
        setCanonicalAttributes(prev =>
          prev.map(attr => attr.id === editingAttribute.id ? data.data : attr)
        );
        setEditingAttribute(null);
        toast({
          title: 'Attribute Updated',
          description: 'Canonical attribute saved successfully',
        });
      } else {
        const error = await response.json();
        throw new Error(error.error || 'Failed to save attribute');
      }
    } catch (error: any) {
      toast({
        title: 'Save Failed',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setSavingAttribute(false);
    }
  };

  // Create new attribute
  const handleCreateAttribute = async () => {
    if (!sectorId || !technicalResearch?.categoryPath) return;

    if (!newAttribute.attribute_name || !newAttribute.canonical_name) {
      toast({
        title: 'Validation Error',
        description: 'Attribute name and canonical name are required',
        variant: 'destructive',
      });
      return;
    }

    setSavingAttribute(true);
    try {
      const response = await authFetch(
        `${API_URL}/sectors/${sectorId}/canonical-attributes`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            product_category: technicalResearch.categoryPath,
            attribute_name: newAttribute.attribute_name,
            canonical_name: newAttribute.canonical_name,
            data_type: newAttribute.data_type || 'string',
            unit: newAttribute.unit,
            typical_min: newAttribute.typical_min,
            typical_max: newAttribute.typical_max,
            enum_values: newAttribute.enum_values,
            description: newAttribute.description,
          }),
        }
      );

      if (response.ok) {
        const data = await response.json();
        setCanonicalAttributes(prev => [...prev, data.data]);
        setShowNewAttributeForm(false);
        setNewAttribute({ data_type: 'string', enum_values: [] });
        toast({
          title: 'Attribute Created',
          description: 'New canonical attribute created successfully',
        });
      } else {
        const error = await response.json();
        throw new Error(error.error || 'Failed to create attribute');
      }
    } catch (error: any) {
      toast({
        title: 'Create Failed',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setSavingAttribute(false);
    }
  };

  // Delete attribute
  const handleDeleteAttribute = async (id: string) => {
    if (!sectorId) return;

    setDeletingAttributeId(id);
    try {
      const response = await authFetch(
        `${API_URL}/sectors/${sectorId}/canonical-attributes/${id}`,
        { method: 'DELETE' }
      );

      if (response.ok) {
        setCanonicalAttributes(prev => prev.filter(attr => attr.id !== id));
        toast({
          title: 'Attribute Deleted',
          description: 'Canonical attribute deleted successfully',
        });
      } else {
        const error = await response.json();
        throw new Error(error.error || 'Failed to delete attribute');
      }
    } catch (error: any) {
      toast({
        title: 'Delete Failed',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setDeletingAttributeId(null);
    }
  };

  // Add enum value to editing attribute
  const handleAddEnumValue = (value: string) => {
    if (!value.trim()) return;

    if (editingAttribute) {
      const currentValues = editingAttribute.enum_values || [];
      if (!currentValues.includes(value.trim())) {
        setEditingAttribute({
          ...editingAttribute,
          enum_values: [...currentValues, value.trim()],
        });
      }
    } else if (showNewAttributeForm) {
      const currentValues = newAttribute.enum_values || [];
      if (!currentValues.includes(value.trim())) {
        setNewAttribute({
          ...newAttribute,
          enum_values: [...currentValues, value.trim()],
        });
      }
    }
    setNewEnumValue('');
  };

  // Remove enum value from editing attribute
  const handleRemoveEnumValue = (value: string) => {
    if (editingAttribute) {
      setEditingAttribute({
        ...editingAttribute,
        enum_values: (editingAttribute.enum_values || []).filter(v => v !== value),
      });
    } else if (showNewAttributeForm) {
      setNewAttribute({
        ...newAttribute,
        enum_values: (newAttribute.enum_values || []).filter(v => v !== value),
      });
    }
  };

  // Fetch AI suggestions for attribute values
  const handleAskAI = async () => {
    if (!editingAttribute?.id || !sectorId) return;

    setLoadingAiSuggestions(true);
    setAiSuggestions(null);

    try {
      const response = await authFetch(
        `/api/sectors/${sectorId}/canonical-attributes/${editingAttribute.id}/suggest`,
        { method: 'POST' }
      );

      if (!response.ok) {
        throw new Error('Failed to get AI suggestions');
      }

      const data = await response.json();
      setAiSuggestions({
        unit: data.suggestions?.unit,
        typical_min: data.suggestions?.typical_min,
        typical_max: data.suggestions?.typical_max,
        description: data.suggestions?.description,
        enum_values: data.suggestions?.enum_values,
        reasoning: data.reasoning,
        sample_values_found: data.sample_values_found,
        based_on_research: data.based_on_research,
        research_title: data.research_title
      });

      toast({
        title: 'AI Suggestions Ready',
        description: 'Review the suggested values below. Click "Apply" to use them.',
      });
    } catch (error) {
      console.error('Error fetching AI suggestions:', error);
      toast({
        title: 'Failed to get suggestions',
        description: error instanceof Error ? error.message : 'Unknown error',
        variant: 'destructive',
      });
    } finally {
      setLoadingAiSuggestions(false);
    }
  };

  // Apply a single AI suggestion to the editing attribute
  const applyAiSuggestion = (field: string, value: unknown) => {
    if (!editingAttribute) return;
    setEditingAttribute({
      ...editingAttribute,
      [field]: value,
    });
    // Remove from suggestions after applying
    setAiSuggestions(prev => prev ? { ...prev, [field]: undefined } : null);
    toast({
      title: 'Suggestion Applied',
      description: `${field.replace('_', ' ')} has been updated`,
    });
  };

  // Apply all AI suggestions at once
  const applyAllAiSuggestions = () => {
    if (!editingAttribute || !aiSuggestions) return;

    const updates: Partial<CanonicalAttribute> = {};
    if (aiSuggestions.unit !== undefined && aiSuggestions.unit !== null) updates.unit = aiSuggestions.unit;
    if (aiSuggestions.typical_min !== undefined && aiSuggestions.typical_min !== null) updates.typical_min = aiSuggestions.typical_min;
    if (aiSuggestions.typical_max !== undefined && aiSuggestions.typical_max !== null) updates.typical_max = aiSuggestions.typical_max;
    if (aiSuggestions.description !== undefined && aiSuggestions.description !== null) updates.description = aiSuggestions.description;
    if (aiSuggestions.enum_values !== undefined && aiSuggestions.enum_values !== null) updates.enum_values = aiSuggestions.enum_values;

    setEditingAttribute({ ...editingAttribute, ...updates });
    setAiSuggestions(null);
    toast({
      title: 'All Suggestions Applied',
      description: 'Review the values and click Save to confirm',
    });
  };

  // Dismiss AI suggestions
  const dismissAiSuggestions = () => {
    setAiSuggestions(null);
  };

  // Batch research functions
  const handleStartBatchResearch = async () => {
    if (!sectorId || selectedBatchCategories.length === 0) return;

    // Immediately show loading state
    setStartingBatchResearch(true);

    try {
      const categoriesToResearch = availableCategories
        .filter(c => selectedBatchCategories.includes(c.categoryPath))
        .map(c => ({ categoryPath: c.categoryPath, categoryName: c.categoryName }));

      const response = await authFetch(
        `${API_URL}/category-characteristics/${sectorId}/batch-research`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            categories: categoriesToResearch,
            siteId: selectedSite?.id,
            researchSteps: batchResearchSteps,
          }),
        }
      );

      if (response.ok) {
        const data = await response.json();
        setBatchResearchJob({
          id: data.jobId,
          status: 'running',
          totalCategories: categoriesToResearch.length,
          completedCategories: 0,
          failedCategories: 0,
          currentStep: null,
          progressPercent: 0,
          progressDetails: {},
          lastError: null,
        });
        setBatchResearchPolling(true);
        toast({
          title: 'Batch Research Started',
          description: `Processing ${categoriesToResearch.length} categories in the background`,
        });
      } else {
        const error = await response.json();
        throw new Error(error.error);
      }
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setStartingBatchResearch(false);
    }
  };

  const pollBatchResearchStatus = useCallback(async (jobId: string) => {
    try {
      const response = await authFetch(
        `${API_URL}/category-characteristics/${sectorId}/batch-research/${jobId}`
      );

      if (response.ok) {
        const data = await response.json();
        setBatchResearchJob({
          id: data.job.id,
          status: data.job.status,
          totalCategories: data.job.totalCategories,
          completedCategories: data.job.completedCategories,
          failedCategories: data.job.failedCategories,
          currentStep: data.job.currentStep,
          progressPercent: data.job.progressPercent,
          progressDetails: data.job.progressDetails || {},
          lastError: data.job.lastError,
        });

        // Stop polling if job is complete or failed
        if (['completed', 'failed', 'cancelled'].includes(data.job.status)) {
          setBatchResearchPolling(false);
          if (data.job.status === 'completed') {
            // Check if enrichment-relevant steps were included
            const enrichmentSteps = ['extraction_hints', 'intelligence'];
            const ranEnrichmentSteps = batchResearchSteps.some(s => enrichmentSteps.includes(s));
            const enrichmentHint = ranEnrichmentSteps
              ? ' New extraction/intelligence research available — consider re-enriching affected products.'
              : '';

            toast({
              title: 'Batch Research Complete',
              description: `Completed ${data.job.completedCategories} categories (${data.job.failedCategories} failed).${enrichmentHint}`,
              duration: ranEnrichmentSteps ? 10000 : 5000,
            });
            loadData(); // Refresh the main data
          }
        }
      }
    } catch (error) {
      console.error('Error polling batch research status:', error);
    }
  }, [sectorId, loadData, toast, batchResearchSteps]);

  // Poll for batch research status
  useEffect(() => {
    if (!batchResearchPolling || !batchResearchJob?.id) return;

    const interval = setInterval(() => {
      pollBatchResearchStatus(batchResearchJob.id);
    }, 3000);

    return () => clearInterval(interval);
  }, [batchResearchPolling, batchResearchJob?.id, pollBatchResearchStatus]);

  const handleCancelBatchResearch = async () => {
    if (!sectorId || !batchResearchJob?.id) return;

    try {
      const response = await authFetch(
        `${API_URL}/category-characteristics/${sectorId}/batch-research/${batchResearchJob.id}/cancel`,
        { method: 'POST' }
      );

      if (response.ok) {
        setBatchResearchPolling(false);
        toast({
          title: 'Job Cancelled',
          description: 'Batch research job has been cancelled',
        });
      }
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    }
  };

  const handleSelectAllCategories = () => {
    if (selectedBatchCategories.length === availableCategories.length) {
      setSelectedBatchCategories([]);
    } else {
      setSelectedBatchCategories(availableCategories.map(c => c.categoryPath));
    }
  };

  const handleSelectUnresearchedCategories = () => {
    const unresearched = availableCategories
      .filter(c => !c.hasResearch)
      .map(c => c.categoryPath);
    setSelectedBatchCategories(unresearched);
  };

  const toggleBatchCategory = (categoryPath: string) => {
    setSelectedBatchCategories(prev =>
      prev.includes(categoryPath)
        ? prev.filter(p => p !== categoryPath)
        : [...prev, categoryPath]
    );
  };

  const toggleBatchStep = (step: string) => {
    setBatchResearchSteps(prev =>
      prev.includes(step)
        ? prev.filter(s => s !== step)
        : [...prev, step]
    );
  };

  // Knowledge Chat Handlers
  const handleChatSend = async () => {
    const writeSectorId = getWriteSectorId();
    if (!chatMessage.trim() || !writeSectorId || !selectedCategory) return;

    setChatLoading(true);
    setChatParsedIntent(null);

    try {
      const response = await authFetch(
        `${API_URL}/category-characteristics/${writeSectorId}/category/${encodeURIComponent(selectedCategory.categoryPath)}/chat`,
        {
          method: 'POST',
          body: JSON.stringify({ message: chatMessage, execute: false }),
        }
      );

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Failed to parse request');
      }

      if (!data.understood) {
        // Add to history as clarification needed
        setChatHistory(prev => [
          ...prev,
          { role: 'user', content: chatMessage },
          { role: 'assistant', content: data.clarificationNeeded || 'Could you please clarify your request?' },
        ]);
        setChatMessage('');
      } else {
        // Show parsed intent for confirmation
        setChatParsedIntent(data.parsed);
        setChatHistory(prev => [
          ...prev,
          { role: 'user', content: chatMessage },
        ]);
      }
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setChatLoading(false);
    }
  };

  const handleChatExecute = async () => {
    const writeSectorId = getWriteSectorId();
    if (!chatParsedIntent || !writeSectorId || !selectedCategory) return;

    setChatExecuting(true);

    try {
      const response = await authFetch(
        `${API_URL}/category-characteristics/${writeSectorId}/category/${encodeURIComponent(selectedCategory.categoryPath)}/chat`,
        {
          method: 'POST',
          body: JSON.stringify({ message: chatMessage, execute: true }),
        }
      );

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Failed to execute operation');
      }

      // Add result to history
      setChatHistory(prev => [
        ...prev,
        {
          role: 'assistant',
          content: chatParsedIntent.confirmationMessage,
          result: { success: true, message: data.result?.message || 'Operation completed successfully' },
        },
      ]);

      toast({
        title: 'Success',
        description: data.result?.message || 'Operation completed successfully',
      });

      // Refresh category data and update selected category
      const categoryPath = selectedCategory.categoryPath;
      await loadData();

      // Fetch fresh category data to update the dialog (from the correct sector)
      const categoryResponse = await authFetch(
        `${API_URL}/category-characteristics/${writeSectorId}/category/${encodeURIComponent(categoryPath)}`
      );
      if (categoryResponse.ok) {
        const categoryData = await categoryResponse.json();
        if (categoryData.data) {
          setSelectedCategory(categoryData.data);
        }
      }

      // Clear state
      setChatMessage('');
      setChatParsedIntent(null);

    } catch (error: any) {
      setChatHistory(prev => [
        ...prev,
        {
          role: 'assistant',
          content: chatParsedIntent.confirmationMessage,
          result: { success: false, error: error.message },
        },
      ]);
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setChatExecuting(false);
    }
  };

  // Load active batch job on mount
  useEffect(() => {
    const loadActiveJob = async () => {
      if (!sectorId) return;
      try {
        const response = await authFetch(
          `${API_URL}/category-characteristics/${sectorId}/batch-research?status=running&limit=1`
        );
        if (response.ok) {
          const data = await response.json();
          if (data.jobs?.length > 0) {
            const job = data.jobs[0];
            setBatchResearchJob({
              id: job.id,
              status: job.status,
              totalCategories: job.totalCategories,
              completedCategories: job.completedCategories,
              failedCategories: job.failedCategories,
              currentStep: job.currentStep,
              progressPercent: job.progressPercent,
              progressDetails: {},
              lastError: job.lastError,
            });
            setBatchResearchPolling(true);
          }
        }
      } catch (error) {
        console.error('Error loading active batch job:', error);
      }
    };
    loadActiveJob();
  }, [sectorId]);

  // Filter canonical attributes by search
  const filteredCanonicalAttributes = canonicalAttributes.filter(attr => {
    if (!canonicalSearch.trim()) return true;
    const search = canonicalSearch.toLowerCase();
    return (
      attr.attribute_name.toLowerCase().includes(search) ||
      attr.canonical_name.toLowerCase().includes(search) ||
      (attr.description?.toLowerCase().includes(search))
    );
  });

  const filteredCategories = categories.filter(cat => {
    if (statusFilter === 'all') return true;
    if (statusFilter === 'stale') return cat.freshness.status === 'stale' || cat.freshness.status === 'aging';
    return cat.status === statusFilter;
  });

  const filteredAvailableCategories = availableCategories.filter(cat => {
    if (statusFilter === 'all') return true;
    if (statusFilter === 'researched') return cat.hasResearch;
    if (statusFilter === 'not_researched') return !cat.hasResearch;
    return true;
  });

  if (!isSuperAdmin) {
    return (
      <div className="container mx-auto p-6">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Access denied. This page is only available to Super Admins.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  if (!sectorId) {
    return (
      <div className="container mx-auto p-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Layers className="h-6 w-6" />
              Category Knowledge
            </CardTitle>
            <CardDescription>
              Sector-level AI-researched category characteristics
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                No sector assigned to the selected site. Select a sector below to manage category research.
              </AlertDescription>
            </Alert>
            <div className="flex items-center gap-4">
              <Label className="font-medium">Select Sector:</Label>
              <Select
                value={selectedSectorId?.toString() || ''}
                onValueChange={(v) => setSelectedSectorId(v ? parseInt(v, 10) : null)}
              >
                <SelectTrigger className="w-[300px]">
                  <SelectValue placeholder="Choose a sector to research..." />
                </SelectTrigger>
                <SelectContent>
                  {sectors.map(s => (
                    <SelectItem key={s.id} value={s.id.toString()}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Find current sector name for display
  const currentSectorName = sectors.find(s => s.id.toString() === sectorId?.toString())?.name ||
    (siteSectorId ? `Sector ${siteSectorId}` : 'Unknown');

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Layers className="h-6 w-6" />
            Category Knowledge
          </h1>
          <p className="text-muted-foreground">
            Sector-level AI-researched category characteristics (shared by all sites in this sector)
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={loadData}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          <Button
            variant="default"
            onClick={() => setBatchResearchDialogOpen(true)}
            className="bg-green-600 hover:bg-green-700"
          >
            <ListChecks className="h-4 w-4 mr-2" />
            Complete All Research
          </Button>
          <Button variant="destructive" onClick={handleMarkAllStale}>
            <AlertTriangle className="h-4 w-4 mr-2" />
            Mark All Stale
          </Button>
        </div>
      </div>

      {/* Active Batch Job Progress */}
      {batchResearchJob && batchResearchJob.status === 'running' && (
        <Card className="border-blue-200 bg-blue-50 dark:bg-blue-950/20">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Loader2 className="h-5 w-5 animate-spin text-blue-600" />
                <span className="font-medium">Batch Research in Progress</span>
                <Badge variant="secondary">
                  {batchResearchJob.completedCategories}/{batchResearchJob.totalCategories} categories
                </Badge>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">
                  {batchResearchJob.currentStep && `Current: ${batchResearchJob.currentStep.replace('_', ' ')}`}
                </span>
                <Button size="sm" variant="outline" onClick={handleCancelBatchResearch}>
                  <Square className="h-4 w-4 mr-1" />
                  Cancel
                </Button>
              </div>
            </div>
            <Progress value={batchResearchJob.progressPercent} className="h-2" />
            <div className="flex justify-between mt-1 text-xs text-muted-foreground">
              <span>{batchResearchJob.progressPercent}% complete</span>
              {batchResearchJob.failedCategories > 0 && (
                <span className="text-red-600">{batchResearchJob.failedCategories} failed</span>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Sector Selection - allows managing any sector */}
      <Card>
        <CardContent className="pt-4">
          <div className="flex flex-col gap-4">
            {/* Site's sectors quick access */}
            {selectedSite?.sector_ids && selectedSite.sector_ids.length > 1 && (
              <div className="flex items-center gap-2">
                <Label className="font-medium whitespace-nowrap text-muted-foreground">Site sectors:</Label>
                <div className="flex gap-2">
                  {selectedSite.sector_ids.map(sId => {
                    const sectorIdNum = typeof sId === 'string' ? parseInt(sId, 10) : sId;
                    const sector = sectors.find(s => s.id.toString() === sectorIdNum.toString());
                    const isActive = sectorId === sectorIdNum;
                    return (
                      <Button
                        key={sectorIdNum}
                        variant={isActive ? "default" : "outline"}
                        size="sm"
                        onClick={() => setSelectedSectorId(sectorIdNum)}
                        className={cn(
                          "transition-all",
                          isActive && "ring-2 ring-offset-2 ring-primary"
                        )}
                      >
                        {sector?.name || `Sector ${sectorIdNum}`}
                      </Button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Full sector selector and actions */}
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <Label className="font-medium whitespace-nowrap">Sector:</Label>
                <Select
                  value={selectedSectorId?.toString() || '__current__'}
                  onValueChange={(v) => setSelectedSectorId(v === '__current__' ? null : parseInt(v, 10))}
                >
                  <SelectTrigger className="w-[300px]">
                    <SelectValue placeholder={siteSectorId ? `Current site: ${currentSectorName}` : 'Select sector...'} />
                  </SelectTrigger>
                  <SelectContent>
                    {siteSectorId && (
                      <SelectItem value="__current__">Current site's sector ({currentSectorName})</SelectItem>
                    )}
                    {sectors.map(s => (
                      <SelectItem key={s.id} value={s.id.toString()}>{s.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {selectedSectorId && selectedSectorId !== siteSectorId && (
                  <Badge variant="outline" className="bg-blue-50 dark:bg-blue-950/30 text-blue-700 dark:text-blue-300">
                    Managing: {currentSectorName}
                  </Badge>
                )}
              </div>

              {/* Manual category research */}
              <div className="flex items-center gap-2">
                <Input
                  placeholder="New category name (e.g., DC Motors)"
                  value={newCategoryName}
                  onChange={(e) => setNewCategoryName(e.target.value)}
                  className="w-[250px]"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && newCategoryName.trim()) {
                      handleResearchNewCategory();
                    }
                  }}
                />
                <Button
                  onClick={handleResearchNewCategory}
                  disabled={!newCategoryName.trim() || triggeringResearch === newCategoryName.trim()}
                >
                  {triggeringResearch === newCategoryName.trim() ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Plus className="h-4 w-4 mr-2" />
                  )}
                  Research Category
                </Button>
              </div>
            </div>

            {/* Sector mismatch warning */}
            {sectorMismatchWarning && selectedCategory && (
              <Alert variant="default" className="bg-amber-50 dark:bg-amber-950/30 border-amber-200">
                <AlertTriangle className="h-4 w-4 text-amber-600" />
                <AlertDescription className="text-amber-800 dark:text-amber-200">
                  {sectorMismatchWarning}
                </AlertDescription>
              </Alert>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Summary Cards */}
      {summary && (() => {
        const fullyResearched = categories.filter(c =>
          c.researchDimensions && c.researchDimensions.every(Boolean)
        ).length;
        const totalWithDims = categories.filter(c => c.researchDimensions).length;

        return (
          <div className="grid grid-cols-6 gap-4">
            <Card>
              <CardContent className="pt-4">
                <div className="text-3xl font-bold">{summary.total}</div>
                <div className="text-sm text-muted-foreground">Total Categories</div>
              </CardContent>
            </Card>
            <Card className={cn(
              fullyResearched === totalWithDims && totalWithDims > 0
                ? 'border-green-200 bg-green-50 dark:bg-green-950/20'
                : 'border-blue-200 bg-blue-50 dark:bg-blue-950/20'
            )}>
              <CardContent className="pt-4">
                <div className={cn(
                  'text-3xl font-bold',
                  fullyResearched === totalWithDims && totalWithDims > 0 ? 'text-green-700' : 'text-blue-700'
                )}>
                  {fullyResearched}/{totalWithDims}
                </div>
                <div className="text-sm text-muted-foreground">Fully Researched (7/7)</div>
              </CardContent>
            </Card>
            <Card className="border-green-200 bg-green-50 dark:bg-green-950/20">
              <CardContent className="pt-4">
                <div className="text-3xl font-bold text-green-700">{summary.byStatus.active}</div>
                <div className="text-sm text-green-600">Active</div>
              </CardContent>
            </Card>
            <Card className="border-yellow-200 bg-yellow-50 dark:bg-yellow-950/20">
              <CardContent className="pt-4">
                <div className="text-3xl font-bold text-yellow-700">{summary.byStatus.stale}</div>
                <div className="text-sm text-yellow-600">Stale</div>
              </CardContent>
            </Card>
            <Card className="border-gray-200 bg-gray-50 dark:bg-gray-950/20">
              <CardContent className="pt-4">
                <div className="text-3xl font-bold text-gray-700">{summary.byStatus.pending}</div>
                <div className="text-sm text-gray-600">Pending</div>
              </CardContent>
            </Card>
            <Card className="border-red-200 bg-red-50 dark:bg-red-950/20">
              <CardContent className="pt-4">
                <div className="text-3xl font-bold text-red-700">{summary.byStatus.failed}</div>
                <div className="text-sm text-red-600">Failed</div>
              </CardContent>
            </Card>
          </div>
        );
      })()}

      {/* Tabs for Catalog, GPT Browser, and Researched */}
      <Tabs value={viewMode} onValueChange={(v) => setViewMode(v as 'catalog' | 'gpt-browser' | 'researched')}>
        <div className="flex items-center justify-between">
          <TabsList>
            <TabsTrigger value="catalog">
              Your Catalog ({filteredAvailableCategories.length})
            </TabsTrigger>
            <TabsTrigger value="gpt-browser">
              Browse GPT Taxonomy
            </TabsTrigger>
            <TabsTrigger value="researched">
              Researched ({categories.length})
            </TabsTrigger>
          </TabsList>

          {viewMode === 'researched' && (
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="stale">Stale / Aging</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="failed">Failed</SelectItem>
              </SelectContent>
            </Select>
          )}
        </div>

        {/* Catalog Categories Tab - Categories where site has products */}
        <TabsContent value="catalog" className="mt-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Your Catalog Categories</CardTitle>
              <CardDescription>
                Categories from your product feed that belong to the selected sector's taxonomy.
                Research is stored under the selected sector.
              </CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              {loading ? (
                <div className="p-6 space-y-4">
                  {[...Array(5)].map((_, i) => (
                    <Skeleton key={i} className="h-16 w-full" />
                  ))}
                </div>
              ) : filteredAvailableCategories.length === 0 ? (
                <div className="p-12 text-center text-muted-foreground">
                  <Layers className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No product categories found in this sector's taxonomy</p>
                  <p className="text-sm mt-2">
                    This can happen if:
                    <br />• Your products use a different Google taxonomy path
                    <br />• The sector's taxonomy root doesn't match your product categories
                  </p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Category</TableHead>
                      <TableHead>Products</TableHead>
                      <TableHead>Brands</TableHead>
                      <TableHead>Price Range</TableHead>
                      <TableHead>Research Status</TableHead>
                      <TableHead className="w-32">Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredAvailableCategories.map((cat) => (
                      <TableRow key={cat.categoryPath}>
                        <TableCell>
                          <div>
                            <div className="font-medium">{cat.categoryName}</div>
                            <div className="text-xs text-muted-foreground font-mono truncate max-w-xs">
                              {cat.categoryPath}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="secondary">{cat.productCount} products</Badge>
                        </TableCell>
                        <TableCell>
                          <span className="text-sm">{cat.brandCount} brands</span>
                        </TableCell>
                        <TableCell>
                          {cat.priceRange ? (
                            <span className="text-sm">
                              €{cat.priceRange.min.toFixed(0)} - €{cat.priceRange.max.toFixed(0)}
                            </span>
                          ) : (
                            <span className="text-muted-foreground">-</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {cat.hasResearch ? (
                            <Badge variant="default" className="bg-green-600">
                              {cat.researchStatus}
                            </Badge>
                          ) : (
                            <Badge variant="outline">Not researched</Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          <Button
                            size="sm"
                            variant={cat.hasResearch ? 'outline' : 'default'}
                            onClick={() => handleTriggerResearch(cat.categoryPath)}
                            disabled={triggeringResearch === cat.categoryPath}
                          >
                            {triggeringResearch === cat.categoryPath ? (
                              <>
                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                Researching...
                              </>
                            ) : cat.hasResearch ? (
                              <>
                                <RefreshCw className="h-4 w-4 mr-2" />
                                Refresh
                              </>
                            ) : (
                              <>
                                <Sparkles className="h-4 w-4 mr-2" />
                                Research
                              </>
                            )}
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* GPT Taxonomy Browser Tab */}
        <TabsContent value="gpt-browser" className="mt-4">
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-base flex items-center gap-2">
                    <Database className="h-4 w-4" />
                    Browse Google Product Taxonomy
                  </CardTitle>
                  <CardDescription>
                    Research any category from Google's taxonomy. Select a sector below to determine where research is stored.
                  </CardDescription>
                </div>
              </div>
              {/* Sector selector for GPT browser - restricted to site's sectors */}
              <div className="flex items-center gap-4 mt-4 pt-4 border-t">
                <Label className="font-medium whitespace-nowrap">Research for Sector:</Label>
                {selectedSite?.sector_ids && selectedSite.sector_ids.length > 0 ? (
                  <div className="flex gap-2">
                    {selectedSite.sector_ids.map(sId => {
                      const sectorIdNum = typeof sId === 'string' ? parseInt(sId, 10) : sId;
                      const sector = sectors.find(s => s.id.toString() === sectorIdNum.toString());
                      const isActive = sectorId === sectorIdNum;
                      return (
                        <Button
                          key={sectorIdNum}
                          variant={isActive ? "default" : "outline"}
                          size="sm"
                          onClick={() => {
                            setSelectedSectorId(sectorIdNum);
                            // Reset GPT browser state when sector changes
                            setGptBreadcrumb([]);
                            setGptTaxonomy([]);
                          }}
                          className={cn(
                            "transition-all",
                            isActive && "ring-2 ring-offset-2 ring-primary"
                          )}
                        >
                          {sector?.name || `Sector ${sectorIdNum}`}
                        </Button>
                      );
                    })}
                  </div>
                ) : (
                  <span className="text-muted-foreground text-sm">No sectors assigned to this site</span>
                )}
              </div>
            </CardHeader>

            <CardContent>
              {/* Breadcrumb navigation */}
              {gptBreadcrumb.length > 0 && (
                <div className="flex items-center gap-1 mb-4 text-sm flex-wrap">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleGptBreadcrumbClick(-1)}
                    className="h-7 px-2"
                  >
                    Root
                  </Button>
                  {gptBreadcrumb.map((crumb, index) => (
                    <React.Fragment key={crumb.id}>
                      <ChevronRight className="h-4 w-4 text-muted-foreground" />
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleGptBreadcrumbClick(index)}
                        className={cn(
                          "h-7 px-2",
                          index === gptBreadcrumb.length - 1 && "font-semibold"
                        )}
                      >
                        {crumb.category_name}
                      </Button>
                    </React.Fragment>
                  ))}
                </div>
              )}

              {/* Load taxonomy button if not loaded */}
              {gptTaxonomy.length === 0 && !gptLoading && (
                <div className="text-center py-8">
                  <Database className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p className="text-muted-foreground mb-4">Click to load the taxonomy tree for this sector</p>
                  <Button onClick={() => loadGptTaxonomy()}>
                    <Eye className="h-4 w-4 mr-2" />
                    Load Taxonomy
                  </Button>
                </div>
              )}

              {gptLoading ? (
                <div className="space-y-2">
                  {[...Array(5)].map((_, i) => (
                    <Skeleton key={i} className="h-10 w-full" />
                  ))}
                </div>
              ) : gptTaxonomy.length > 0 && (
                <div className="space-y-1">
                  {gptTaxonomy.map((cat) => {
                    // Check if this category is already researched
                    const isResearched = categories.some(
                      c => c.categoryName === cat.category_name ||
                           c.categoryPath.endsWith(cat.category_name)
                    );
                    return (
                      <div
                        key={cat.id}
                        className="flex items-center justify-between p-2 rounded-md hover:bg-muted/50 group"
                      >
                        <button
                          onClick={() => handleGptCategoryClick(cat)}
                          className="flex items-center gap-2 text-left flex-1"
                        >
                          <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:text-foreground" />
                          <span className="font-medium">{cat.category_name}</span>
                          {cat.avg_monthly_searches && (
                            <Badge variant="outline" className="text-xs">
                              {parseInt(cat.avg_monthly_searches).toLocaleString()} searches/mo
                            </Badge>
                          )}
                        </button>
                        <Button
                          size="sm"
                          variant={isResearched ? "outline" : "default"}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleResearchGptCategory(cat);
                          }}
                          disabled={triggeringResearch !== null}
                        >
                          {isResearched ? (
                            <>
                              <CheckCircle className="h-4 w-4 mr-1 text-green-600" />
                              Researched
                            </>
                          ) : (
                            <>
                              <Sparkles className="h-4 w-4 mr-1" />
                              Research
                            </>
                          )}
                        </Button>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Researched Categories Tab */}
        <TabsContent value="researched" className="mt-4">
          <Card>
            <CardContent className="p-0">
              {loading ? (
                <div className="p-6 space-y-4">
                  {[...Array(5)].map((_, i) => (
                    <Skeleton key={i} className="h-16 w-full" />
                  ))}
                </div>
              ) : filteredCategories.length === 0 ? (
                <div className="p-12 text-center text-muted-foreground">
                  <Layers className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No category characteristics found</p>
                  <p className="text-sm">Research will be triggered automatically during AEO generation</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Category</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Research Dimensions</TableHead>
                      <TableHead>Freshness</TableHead>
                      <TableHead>Usage</TableHead>
                      {/* <TableHead>Cost</TableHead> */}
                      <TableHead className="w-12"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredCategories.map((cat) => {
                      const dims = cat.researchDimensions || [];
                      const completedDims = dims.filter(Boolean).length;
                      const totalDims = dims.length || 7;
                      const missingDims = dimensionShortNames.filter((_, i) => dims[i] === false);
                      const dimTooltip = missingDims.length > 0
                        ? `Missing: ${missingDims.join(', ')}`
                        : 'All research dimensions complete';

                      return (
                        <TableRow
                          key={cat.id}
                          className="cursor-pointer hover:bg-muted/50"
                          onClick={() => setSelectedCategory(cat)}
                        >
                          <TableCell>
                            <div>
                              <div className="font-medium">{cat.categoryName}</div>
                              <div className="text-xs text-muted-foreground font-mono truncate max-w-xs">
                                {cat.categoryPath}
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge
                              variant={
                                cat.status === 'active' ? 'default' :
                                cat.status === 'failed' ? 'destructive' :
                                cat.status === 'researching' ? 'secondary' :
                                'outline'
                              }
                            >
                              {cat.status}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2" title={dimTooltip}>
                              <Progress
                                value={(completedDims / totalDims) * 100}
                                className={cn(
                                  'w-20 h-2',
                                  completedDims === totalDims ? '[&>div]:bg-green-500' :
                                  completedDims <= 2 ? '[&>div]:bg-red-500' :
                                  '[&>div]:bg-yellow-500'
                                )}
                              />
                              <span className={cn(
                                'text-sm font-medium',
                                completedDims === totalDims ? 'text-green-700 dark:text-green-400' :
                                completedDims <= 2 ? 'text-red-700 dark:text-red-400' :
                                'text-yellow-700 dark:text-yellow-400'
                              )}>
                                {completedDims}/{totalDims}
                              </span>
                            </div>
                          </TableCell>
                          <TableCell>
                            <FreshnessIndicator freshness={cat.freshness} />
                          </TableCell>
                          <TableCell>
                            <span className="text-sm">{cat.timesUsed}x</span>
                          </TableCell>
                          {/* Cost column hidden until properly debugged
                          <TableCell>
                            <span className="text-sm">${cat.researchCostUsd.toFixed(4)}</span>
                          </TableCell>
                          */}
                          <TableCell>
                            <ChevronRight className="h-4 w-4 text-muted-foreground" />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Category Detail Dialog */}
      <CategoryDetailDialog
        category={selectedCategory}
        isOpen={!!selectedCategory}
        onClose={() => setSelectedCategory(null)}
        onRefresh={handleRefresh}
        onRefreshAspect={handleRefreshAspect}
        technicalResearch={technicalResearch}
        onRegenerateAttributes={handleRegenerateAttributes}
        onResearchIntelligence={handleResearchIntelligence}
        onResearchExtractionHints={handleResearchExtractionHints}
        onResearchPerceivedBenefits={handleResearchPerceivedBenefits}
        onResearchAttributeMappings={handleResearchAttributeMappings}
        sectorId={sectorId}
        onOpenCanonicalDialog={handleOpenCanonicalDialog}
        onOpenIntelligenceDialog={handleOpenIntelligenceDialog}
        onOpenExtractionHintsDialog={handleOpenExtractionHintsDialog}
        onOpenChatDialog={() => setChatDialogOpen(true)}
      />

      {/* Attribute Intelligence Viewer Dialog */}
      <Dialog open={intelligenceDialogOpen} onOpenChange={setIntelligenceDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Lightbulb className="h-5 w-5" />
              Attribute Intelligence
            </DialogTitle>
            <DialogDescription>
              Value interpretations for "{technicalResearch?.categoryName || 'category'}" - explains what technical spec values mean to customers
            </DialogDescription>
          </DialogHeader>

          <ScrollArea className="h-[500px] pr-4">
            {intelligenceLoading ? (
              <div className="space-y-3">
                {[...Array(3)].map((_, i) => (
                  <Skeleton key={i} className="h-32 w-full" />
                ))}
              </div>
            ) : attributeIntelligence.length === 0 ? (
              <div className="text-center text-muted-foreground py-8">
                <Lightbulb className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p className="font-medium">No attribute intelligence found</p>
                <p className="text-sm mt-1">
                  Use the Category Perception Research to generate value interpretations for technical attributes.
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {attributeIntelligence.map((intel) => (
                  <Card key={intel.id} className={cn(
                    "border",
                    editingIntelligence?.id === intel.id
                      ? "border-blue-400 bg-blue-50/50 dark:bg-blue-950/20"
                      : "border-amber-200 bg-amber-50/50 dark:bg-amber-950/20"
                  )}>
                    <CardHeader className="pb-2">
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-base flex items-center gap-2">
                          <Lightbulb className="h-4 w-4 text-amber-600" />
                          {intel.title}
                        </CardTitle>
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="text-xs">
                            {intel.scope_attribute_name}
                          </Badge>
                          {editingIntelligence?.id !== intel.id && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setEditingIntelligence({ ...intel })}
                              className="h-7 px-2"
                            >
                              <Edit3 className="h-3 w-3 mr-1" />
                              Edit
                            </Button>
                          )}
                        </div>
                      </div>
                      {editingIntelligence?.id === intel.id ? (
                        <div className="mt-2">
                          <Label className="text-xs">Summary (shown in UI)</Label>
                          <Input
                            value={editingIntelligence.knowledge_text}
                            onChange={(e) => setEditingIntelligence({
                              ...editingIntelligence,
                              knowledge_text: e.target.value
                            })}
                            className="mt-1"
                          />
                        </div>
                      ) : (
                        <CardDescription>{intel.knowledge_text}</CardDescription>
                      )}
                    </CardHeader>
                    <CardContent>
                      <div className="bg-white dark:bg-gray-900 p-3 rounded-lg border">
                        <Label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                          Value Interpretations
                        </Label>
                        {editingIntelligence?.id === intel.id ? (
                          <Textarea
                            value={editingIntelligence.research_findings}
                            onChange={(e) => setEditingIntelligence({
                              ...editingIntelligence,
                              research_findings: e.target.value
                            })}
                            className="mt-2 font-mono text-sm min-h-[200px]"
                            placeholder="Enter value interpretations...

Example format:
- 60 TPI: Standard durability. Good for training and commuting.
- 120 TPI: Performance-oriented. Lighter, more supple feel.
- 660 TPI: Downhill/Enduro reinforced casing. Ultra-durable."
                          />
                        ) : (
                          <pre className="mt-2 text-sm whitespace-pre-wrap font-mono text-gray-700 dark:text-gray-300">
                            {intel.research_findings}
                          </pre>
                        )}
                      </div>
                      {editingIntelligence?.id === intel.id && (
                        <div className="flex justify-end gap-2 mt-3">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setEditingIntelligence(null)}
                          >
                            Cancel
                          </Button>
                          <Button
                            size="sm"
                            onClick={handleSaveIntelligence}
                            disabled={savingIntelligence}
                          >
                            {savingIntelligence ? (
                              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            ) : (
                              <Save className="h-4 w-4 mr-2" />
                            )}
                            Save Changes
                          </Button>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </ScrollArea>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIntelligenceDialogOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Extraction Hints Dialog */}
      <Dialog open={extractionHintsDialogOpen} onOpenChange={setExtractionHintsDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wand2 className="h-5 w-5" />
              Extraction Hints
            </DialogTitle>
            <DialogDescription>
              Field disambiguation and qualifier rules for "{technicalResearch?.categoryName || 'category'}" - teaches AI how to correctly extract values from product feeds
            </DialogDescription>
          </DialogHeader>

          <ScrollArea className="h-[500px] pr-4">
            {extractionHintsLoading ? (
              <div className="space-y-3">
                {[...Array(3)].map((_, i) => (
                  <Skeleton key={i} className="h-32 w-full" />
                ))}
              </div>
            ) : extractionHints.length === 0 ? (
              <div className="text-center text-muted-foreground py-8">
                <Wand2 className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p className="font-medium">No extraction hints found</p>
                <p className="text-sm mt-1">
                  Use the "Research Extraction Hints" button to generate field disambiguation rules.
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {extractionHints.map((hint) => (
                  <Card key={hint.specName} className={cn(
                    "border",
                    hint.hasDisambiguation || hint.hasQualifierRules || hint.hasSemanticClarifications
                      ? "border-purple-200 bg-purple-50/50 dark:bg-purple-950/20"
                      : "border-gray-200 bg-gray-50/50 dark:bg-gray-950/20"
                  )}>
                    <CardHeader className="pb-2">
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-base flex items-center gap-2">
                          <Wand2 className="h-4 w-4 text-purple-600" />
                          {hint.displayName}
                          <Badge variant="outline" className="text-xs ml-2 font-mono">
                            {hint.specName}
                          </Badge>
                        </CardTitle>
                        <div className="flex items-center gap-2">
                          {hint.isKeyAttribute && (
                            <Badge className="bg-amber-500 text-xs">Key Attribute</Badge>
                          )}
                          {hint.isMultiSelect && (
                            <Badge variant="secondary" className="text-xs">Multi-Select</Badge>
                          )}
                          {hint.unit && (
                            <Badge variant="outline" className="text-xs">{hint.unit}</Badge>
                          )}
                        </div>
                      </div>
                      {hint.guidance && (
                        <CardDescription className="mt-1">{hint.guidance}</CardDescription>
                      )}
                    </CardHeader>
                    <CardContent className="space-y-3">
                      {/* Field Disambiguation */}
                      {hint.hasDisambiguation && hint.fieldDisambiguation && (
                        <div className="bg-white dark:bg-gray-900 p-3 rounded-lg border">
                          <Label className="text-xs font-semibold text-purple-600 uppercase tracking-wide flex items-center gap-1">
                            <ArrowRight className="h-3 w-3" />
                            Field Disambiguation
                          </Label>
                          <div className="mt-2 space-y-2">
                            {hint.fieldDisambiguation.prefer_fields && hint.fieldDisambiguation.prefer_fields.length > 0 && (
                              <div className="flex flex-wrap gap-1">
                                <span className="text-xs text-green-600 font-medium">PREFER:</span>
                                {hint.fieldDisambiguation.prefer_fields.map((field, i) => (
                                  <Badge key={i} variant="outline" className="text-xs bg-green-50 text-green-800">
                                    {field}
                                  </Badge>
                                ))}
                              </div>
                            )}
                            {hint.fieldDisambiguation.avoid_fields && hint.fieldDisambiguation.avoid_fields.length > 0 && (
                              <div className="flex flex-wrap gap-1">
                                <span className="text-xs text-red-600 font-medium">AVOID:</span>
                                {hint.fieldDisambiguation.avoid_fields.map((field, i) => (
                                  <Badge key={i} variant="outline" className="text-xs bg-red-50 text-red-800">
                                    {field}
                                  </Badge>
                                ))}
                              </div>
                            )}
                            {hint.fieldDisambiguation.notes && (
                              <p className="text-xs text-muted-foreground mt-1">{hint.fieldDisambiguation.notes}</p>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Qualifier Rules */}
                      {hint.hasQualifierRules && hint.qualifierRules && (
                        <div className="bg-white dark:bg-gray-900 p-3 rounded-lg border">
                          <Label className="text-xs font-semibold text-blue-600 uppercase tracking-wide flex items-center gap-1">
                            <Check className="h-3 w-3" />
                            Qualifier Preservation
                          </Label>
                          <div className="mt-2 space-y-2">
                            {hint.qualifierRules.preserve && hint.qualifierRules.preserve.length > 0 && (
                              <div className="flex flex-wrap gap-1">
                                <span className="text-xs text-blue-600 font-medium">PRESERVE:</span>
                                {hint.qualifierRules.preserve.map((q, i) => (
                                  <Badge key={i} variant="outline" className="text-xs bg-blue-50 text-blue-800">
                                    {q}
                                  </Badge>
                                ))}
                              </div>
                            )}
                            {hint.qualifierRules.examples && hint.qualifierRules.examples.length > 0 && (
                              <div className="text-xs text-muted-foreground">
                                Examples: {hint.qualifierRules.examples.join(', ')}
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Semantic Clarifications */}
                      {hint.hasSemanticClarifications && hint.semanticClarifications && (
                        <div className="bg-white dark:bg-gray-900 p-3 rounded-lg border">
                          <Label className="text-xs font-semibold text-amber-600 uppercase tracking-wide flex items-center gap-1">
                            <WarningIcon className="h-3 w-3" />
                            Semantic Clarifications
                          </Label>
                          <div className="mt-2 space-y-2">
                            {hint.semanticClarifications.mislabels && Object.keys(hint.semanticClarifications.mislabels).length > 0 && (
                              <div className="space-y-1">
                                <span className="text-xs text-amber-600 font-medium">MISLABELS:</span>
                                {Object.entries(hint.semanticClarifications.mislabels).map(([key, value], i) => (
                                  <div key={i} className="flex items-center gap-2 text-xs">
                                    <Badge variant="outline" className="bg-amber-50 text-amber-800">{key}</Badge>
                                    <ArrowRight className="h-3 w-3 text-muted-foreground" />
                                    <span className="text-muted-foreground">actually means:</span>
                                    <Badge variant="secondary">{value}</Badge>
                                  </div>
                                ))}
                              </div>
                            )}
                            {hint.semanticClarifications.notes && (
                              <p className="text-xs text-muted-foreground mt-1">{hint.semanticClarifications.notes}</p>
                            )}
                          </div>
                        </div>
                      )}

                      {/* No hints message */}
                      {!hint.hasDisambiguation && !hint.hasQualifierRules && !hint.hasSemanticClarifications && (
                        <div className="text-xs text-muted-foreground italic">
                          No extraction hints generated yet. Research to populate.
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </ScrollArea>

          <DialogFooter>
            <Button variant="outline" onClick={() => setExtractionHintsDialogOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Canonical Attributes Editor Dialog */}
      <Dialog open={canonicalDialogOpen} onOpenChange={setCanonicalDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Database className="h-5 w-5" />
              Canonical Attributes Editor
            </DialogTitle>
            <DialogDescription>
              Manage canonical attribute definitions for "{technicalResearch?.categoryName || 'category'}"
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Search and Add New */}
            <div className="flex items-center gap-2">
              <Input
                placeholder="Search attributes..."
                value={canonicalSearch}
                onChange={(e) => setCanonicalSearch(e.target.value)}
                className="flex-1"
              />
              <Button
                onClick={() => {
                  setShowNewAttributeForm(true);
                  setEditingAttribute(null);
                }}
                disabled={showNewAttributeForm}
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Attribute
              </Button>
            </div>

            {/* New Attribute Form */}
            {showNewAttributeForm && (
              <Card className="border-2 border-blue-300 bg-blue-50 dark:bg-blue-950/20">
                <CardContent className="pt-4 space-y-4">
                  <div className="flex items-center justify-between">
                    <h4 className="font-semibold text-blue-800 dark:text-blue-200">New Attribute</h4>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setShowNewAttributeForm(false);
                        setNewAttribute({ data_type: 'string', enum_values: [] });
                      }}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Attribute Name *</Label>
                      <Input
                        value={newAttribute.attribute_name || ''}
                        onChange={(e) => setNewAttribute({ ...newAttribute, attribute_name: e.target.value })}
                        placeholder="e.g., bead_type"
                      />
                    </div>
                    <div>
                      <Label>Canonical Name *</Label>
                      <Input
                        value={newAttribute.canonical_name || ''}
                        onChange={(e) => setNewAttribute({ ...newAttribute, canonical_name: e.target.value })}
                        placeholder="e.g., Bead Type"
                      />
                    </div>
                    <div>
                      <Label>Data Type</Label>
                      <Select
                        value={newAttribute.data_type || 'string'}
                        onValueChange={(value) => setNewAttribute({ ...newAttribute, data_type: value })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="string">String</SelectItem>
                          <SelectItem value="text">Text</SelectItem>
                          <SelectItem value="number">Number</SelectItem>
                          <SelectItem value="boolean">Boolean</SelectItem>
                          <SelectItem value="enum">Enum</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Unit</Label>
                      <Input
                        value={newAttribute.unit || ''}
                        onChange={(e) => setNewAttribute({ ...newAttribute, unit: e.target.value })}
                        placeholder="e.g., mm, kg, etc."
                      />
                    </div>
                    {(newAttribute.data_type === 'number' || newAttribute.data_type === 'numeric') && (
                      <>
                        <div>
                          <Label>Typical Min</Label>
                          <Input
                            type="number"
                            value={newAttribute.typical_min ?? ''}
                            onChange={(e) => setNewAttribute({ ...newAttribute, typical_min: e.target.value ? Number(e.target.value) : null })}
                            placeholder="e.g., 60"
                          />
                        </div>
                        <div>
                          <Label>Typical Max</Label>
                          <Input
                            type="number"
                            value={newAttribute.typical_max ?? ''}
                            onChange={(e) => setNewAttribute({ ...newAttribute, typical_max: e.target.value ? Number(e.target.value) : null })}
                            placeholder="e.g., 700"
                          />
                        </div>
                      </>
                    )}
                  </div>

                  {/* Enum Values - always show */}
                  <div>
                      <Label>Allowed Values</Label>
                      <div className="flex flex-wrap gap-2 mb-2">
                        {(newAttribute.enum_values || []).map((value, i) => (
                          <Badge key={i} variant="secondary" className="flex items-center gap-1">
                            {value}
                            <button
                              onClick={() => handleRemoveEnumValue(value)}
                              className="ml-1 hover:text-red-500"
                            >
                              <X className="h-3 w-3" />
                            </button>
                          </Badge>
                        ))}
                      </div>
                      <div className="flex gap-2">
                        <Input
                          value={newEnumValue}
                          onChange={(e) => setNewEnumValue(e.target.value)}
                          placeholder="Add allowed value..."
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              handleAddEnumValue(newEnumValue);
                            }
                          }}
                        />
                        <Button
                          variant="outline"
                          onClick={() => handleAddEnumValue(newEnumValue)}
                        >
                          Add
                        </Button>
                      </div>
                    </div>

                  <div>
                    <Label>Description</Label>
                    <Textarea
                      value={newAttribute.description || ''}
                      onChange={(e) => setNewAttribute({ ...newAttribute, description: e.target.value })}
                      placeholder="Description of this attribute..."
                      rows={2}
                    />
                  </div>

                  <div className="flex justify-end gap-2">
                    <Button
                      variant="outline"
                      onClick={() => {
                        setShowNewAttributeForm(false);
                        setNewAttribute({ data_type: 'string', enum_values: [] });
                      }}
                    >
                      Cancel
                    </Button>
                    <Button onClick={handleCreateAttribute} disabled={savingAttribute}>
                      {savingAttribute ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <Save className="h-4 w-4 mr-2" />
                      )}
                      Create Attribute
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Attributes List */}
            <ScrollArea className="h-[400px] pr-4">
              {canonicalLoading ? (
                <div className="space-y-3">
                  {[...Array(5)].map((_, i) => (
                    <Skeleton key={i} className="h-20 w-full" />
                  ))}
                </div>
              ) : filteredCanonicalAttributes.length === 0 ? (
                <div className="text-center text-muted-foreground py-8">
                  {canonicalSearch ? 'No attributes match your search' : 'No canonical attributes defined for this category'}
                </div>
              ) : (
                <div className="space-y-3">
                  {filteredCanonicalAttributes.map((attr) => (
                    <Card
                      key={attr.id}
                      className={cn(
                        'transition-all',
                        editingAttribute?.id === attr.id
                          ? 'border-2 border-blue-400'
                          : 'hover:border-gray-400 cursor-pointer'
                      )}
                      onClick={() => {
                        if (editingAttribute?.id !== attr.id) {
                          // Normalize data_type from DB format ('numeric') to UI format ('number')
                          const normalizedDataType = attr.data_type === 'numeric' ? 'number' : attr.data_type;
                          setEditingAttribute({ ...attr, data_type: normalizedDataType });
                          setShowNewAttributeForm(false);
                          setAiSuggestions(null); // Clear previous suggestions
                        }
                      }}
                    >
                      <CardContent className="pt-4">
                        {editingAttribute?.id === attr.id ? (
                          <div className="space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                              <div>
                                <Label>Attribute Name</Label>
                                <Input
                                  value={editingAttribute.attribute_name}
                                  onChange={(e) => setEditingAttribute({ ...editingAttribute, attribute_name: e.target.value })}
                                />
                              </div>
                              <div>
                                <Label>Canonical Name</Label>
                                <Input
                                  value={editingAttribute.canonical_name}
                                  onChange={(e) => setEditingAttribute({ ...editingAttribute, canonical_name: e.target.value })}
                                />
                              </div>
                              <div>
                                <Label>Data Type</Label>
                                <Select
                                  value={editingAttribute.data_type}
                                  onValueChange={(value) => setEditingAttribute({ ...editingAttribute, data_type: value })}
                                >
                                  <SelectTrigger>
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="string">String</SelectItem>
                                    <SelectItem value="text">Text</SelectItem>
                                    <SelectItem value="number">Number</SelectItem>
                                    <SelectItem value="boolean">Boolean</SelectItem>
                                    <SelectItem value="enum">Enum</SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>
                              <div>
                                <Label>Unit</Label>
                                <Input
                                  value={editingAttribute.unit || ''}
                                  onChange={(e) => setEditingAttribute({ ...editingAttribute, unit: e.target.value || null })}
                                />
                              </div>
                              {(editingAttribute.data_type === 'number' || editingAttribute.data_type === 'numeric') && (
                                <>
                                  <div>
                                    <Label>Typical Min</Label>
                                    <Input
                                      type="number"
                                      value={editingAttribute.typical_min ?? ''}
                                      onChange={(e) => setEditingAttribute({ ...editingAttribute, typical_min: e.target.value ? Number(e.target.value) : null })}
                                      placeholder="e.g., 60"
                                    />
                                  </div>
                                  <div>
                                    <Label>Typical Max</Label>
                                    <Input
                                      type="number"
                                      value={editingAttribute.typical_max ?? ''}
                                      onChange={(e) => setEditingAttribute({ ...editingAttribute, typical_max: e.target.value ? Number(e.target.value) : null })}
                                      placeholder="e.g., 700"
                                    />
                                  </div>
                                </>
                              )}
                            </div>

                            {/* Enum Values Editor - show for all types that can have allowed values */}
                            <div>
                              <Label>Allowed Values (enum_values)</Label>
                                <div className="flex flex-wrap gap-2 mb-2">
                                  {(editingAttribute.enum_values || []).map((value, i) => (
                                    <Badge key={i} variant="secondary" className="flex items-center gap-1">
                                      {value}
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleRemoveEnumValue(value);
                                        }}
                                        className="ml-1 hover:text-red-500"
                                      >
                                        <X className="h-3 w-3" />
                                      </button>
                                    </Badge>
                                  ))}
                                </div>
                                <div className="flex gap-2">
                                  <Input
                                    value={newEnumValue}
                                    onChange={(e) => setNewEnumValue(e.target.value)}
                                    placeholder="Add allowed value..."
                                    onClick={(e) => e.stopPropagation()}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter') {
                                        e.preventDefault();
                                        handleAddEnumValue(newEnumValue);
                                      }
                                    }}
                                  />
                                  <Button
                                    variant="outline"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleAddEnumValue(newEnumValue);
                                    }}
                                  >
                                    Add
                                  </Button>
                                </div>
                            </div>

                            <div>
                              <Label>Description</Label>
                              <Textarea
                                value={editingAttribute.description || ''}
                                onChange={(e) => setEditingAttribute({ ...editingAttribute, description: e.target.value })}
                                rows={2}
                                onClick={(e) => e.stopPropagation()}
                              />
                            </div>

                            {/* AI Suggestions Section */}
                            <div className="border-t border-dashed pt-4 mt-2">
                              <div className="flex items-center justify-between mb-3">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleAskAI();
                                  }}
                                  disabled={loadingAiSuggestions}
                                  className="gap-2"
                                >
                                  {loadingAiSuggestions ? (
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                  ) : (
                                    <Sparkles className="h-4 w-4" />
                                  )}
                                  {loadingAiSuggestions ? 'Analyzing...' : 'Ask AI for suggestions'}
                                </Button>
                                {aiSuggestions && (
                                  <div className="flex gap-2">
                                    <Button
                                      variant="default"
                                      size="sm"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        applyAllAiSuggestions();
                                      }}
                                      className="gap-1"
                                    >
                                      <Check className="h-3 w-3" />
                                      Apply All
                                    </Button>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        dismissAiSuggestions();
                                      }}
                                    >
                                      <X className="h-3 w-3" />
                                    </Button>
                                  </div>
                                )}
                              </div>

                              {/* Show AI suggestions if available */}
                              {aiSuggestions && (
                                <div className={cn(
                                  "rounded-lg p-3 space-y-3 border",
                                  aiSuggestions.based_on_research
                                    ? "bg-green-50 dark:bg-green-950/30 border-green-200 dark:border-green-800"
                                    : "bg-blue-50 dark:bg-blue-950/30 border-blue-200 dark:border-blue-800"
                                )}>
                                  {aiSuggestions.based_on_research && (
                                    <div className="flex items-center gap-2 text-green-700 dark:text-green-300 text-sm font-medium">
                                      <Lightbulb className="h-4 w-4" />
                                      Based on Attribute Intelligence: {aiSuggestions.research_title}
                                    </div>
                                  )}
                                  {aiSuggestions.reasoning && (
                                    <p className={cn(
                                      "text-sm italic",
                                      aiSuggestions.based_on_research
                                        ? "text-green-700 dark:text-green-300"
                                        : "text-blue-700 dark:text-blue-300"
                                    )}>
                                      💡 {aiSuggestions.reasoning}
                                    </p>
                                  )}
                                  {aiSuggestions.sample_values_found && aiSuggestions.sample_values_found.length > 0 && (
                                    <div className="text-xs text-gray-600 dark:text-gray-400">
                                      <span className="font-medium">Values found in products:</span>
                                      <div className="flex flex-wrap gap-1 mt-1">
                                        {aiSuggestions.sample_values_found.slice(0, 12).map((val, i) => (
                                          <Badge key={i} variant="outline" className="text-xs font-mono">
                                            {String(val)}
                                          </Badge>
                                        ))}
                                        {aiSuggestions.sample_values_found.length > 12 && (
                                          <Badge variant="outline" className="text-xs text-gray-400">
                                            +{aiSuggestions.sample_values_found.length - 12} more
                                          </Badge>
                                        )}
                                      </div>
                                    </div>
                                  )}
                                  <div className="grid grid-cols-2 gap-2 text-sm">
                                    {aiSuggestions.unit !== undefined && aiSuggestions.unit !== null && (
                                      <div className="flex items-center justify-between bg-white dark:bg-gray-800 rounded px-2 py-1">
                                        <span><strong>Unit:</strong> {aiSuggestions.unit}</span>
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          className="h-6 w-6 p-0"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            applyAiSuggestion('unit', aiSuggestions.unit);
                                          }}
                                        >
                                          <Check className="h-3 w-3 text-green-600" />
                                        </Button>
                                      </div>
                                    )}
                                    {aiSuggestions.typical_min !== undefined && aiSuggestions.typical_min !== null && (
                                      <div className="flex items-center justify-between bg-white dark:bg-gray-800 rounded px-2 py-1">
                                        <span><strong>Min:</strong> {aiSuggestions.typical_min}</span>
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          className="h-6 w-6 p-0"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            applyAiSuggestion('typical_min', aiSuggestions.typical_min);
                                          }}
                                        >
                                          <Check className="h-3 w-3 text-green-600" />
                                        </Button>
                                      </div>
                                    )}
                                    {aiSuggestions.typical_max !== undefined && aiSuggestions.typical_max !== null && (
                                      <div className="flex items-center justify-between bg-white dark:bg-gray-800 rounded px-2 py-1">
                                        <span><strong>Max:</strong> {aiSuggestions.typical_max}</span>
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          className="h-6 w-6 p-0"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            applyAiSuggestion('typical_max', aiSuggestions.typical_max);
                                          }}
                                        >
                                          <Check className="h-3 w-3 text-green-600" />
                                        </Button>
                                      </div>
                                    )}
                                  </div>
                                  {aiSuggestions.description !== undefined && aiSuggestions.description !== null && (
                                    <div className="flex items-start justify-between bg-white dark:bg-gray-800 rounded px-2 py-1">
                                      <span className="text-sm"><strong>Description:</strong> {aiSuggestions.description}</span>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        className="h-6 w-6 p-0 flex-shrink-0"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          applyAiSuggestion('description', aiSuggestions.description);
                                        }}
                                      >
                                        <Check className="h-3 w-3 text-green-600" />
                                      </Button>
                                    </div>
                                  )}
                                  {aiSuggestions.enum_values && aiSuggestions.enum_values.length > 0 && (
                                    <div className="flex items-start justify-between bg-white dark:bg-gray-800 rounded px-2 py-1">
                                      <span className="text-sm"><strong>Allowed Values:</strong> {aiSuggestions.enum_values.join(', ')}</span>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        className="h-6 w-6 p-0 flex-shrink-0"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          applyAiSuggestion('enum_values', aiSuggestions.enum_values);
                                        }}
                                      >
                                        <Check className="h-3 w-3 text-green-600" />
                                      </Button>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>

                            <div className="flex justify-between">
                              <Button
                                variant="destructive"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (confirm('Are you sure you want to delete this attribute?')) {
                                    handleDeleteAttribute(attr.id);
                                  }
                                }}
                                disabled={deletingAttributeId === attr.id}
                              >
                                {deletingAttributeId === attr.id ? (
                                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                ) : (
                                  <Trash2 className="h-4 w-4 mr-2" />
                                )}
                                Delete
                              </Button>
                              <div className="flex gap-2">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setEditingAttribute(null);
                                  }}
                                >
                                  Cancel
                                </Button>
                                <Button
                                  size="sm"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleSaveAttribute();
                                  }}
                                  disabled={savingAttribute}
                                >
                                  {savingAttribute ? (
                                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                  ) : (
                                    <Save className="h-4 w-4 mr-2" />
                                  )}
                                  Save
                                </Button>
                              </div>
                            </div>
                          </div>
                        ) : (
                          <div className="flex items-start justify-between">
                            <div>
                              <div className="flex items-center gap-2 mb-1">
                                <span className="font-medium">{attr.canonical_name}</span>
                                <Badge variant="outline" className="text-xs">
                                  {attr.data_type}
                                </Badge>
                                {attr.unit && (
                                  <Badge variant="secondary" className="text-xs">
                                    {attr.unit}
                                  </Badge>
                                )}
                              </div>
                              <div className="text-xs text-muted-foreground font-mono mb-1">
                                {attr.attribute_name}
                              </div>
                              {attr.description && (
                                <div className="text-sm text-muted-foreground">
                                  {attr.description}
                                </div>
                              )}
                              {attr.enum_values && attr.enum_values.length > 0 && (
                                <div className="flex flex-wrap gap-1 mt-2">
                                  {attr.enum_values.slice(0, 5).map((val, i) => (
                                    <Badge key={i} variant="secondary" className="text-xs">
                                      {val}
                                    </Badge>
                                  ))}
                                  {attr.enum_values.length > 5 && (
                                    <Badge variant="outline" className="text-xs">
                                      +{attr.enum_values.length - 5} more
                                    </Badge>
                                  )}
                                </div>
                              )}
                            </div>
                            <Edit3 className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </ScrollArea>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setCanonicalDialogOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Batch Research Dialog */}
      <Dialog open={batchResearchDialogOpen} onOpenChange={setBatchResearchDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ListChecks className="h-5 w-5" />
              Complete Category Research
            </DialogTitle>
            <DialogDescription>
              Select categories to research and choose which research steps to run.
              This will process categories in the background - you can close this dialog and continue working.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Research Steps Selection */}
            <div>
              <Label className="text-sm font-medium mb-2 block">Research Steps to Run</Label>
              <div className="flex flex-wrap gap-2">
                {[
                  { id: 'characteristics', label: 'Basic Characteristics', description: 'Product types, brand tiers, use cases, personas' },
                  { id: 'canonical', label: 'Canonical Attributes', description: 'Technical spec definitions' },
                  { id: 'perceived_benefits', label: 'Perceived Benefits', description: 'Customer-facing benefit descriptions' },
                  { id: 'intelligence', label: 'Attribute Intelligence', description: 'Value interpretation guidance' },
                  { id: 'extraction_hints', label: 'Extraction Hints', description: 'Field disambiguation rules' },
                ].map(step => (
                  <div
                    key={step.id}
                    className={cn(
                      'flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer transition-all',
                      batchResearchSteps.includes(step.id)
                        ? 'bg-green-50 border-green-300 dark:bg-green-950/30 dark:border-green-700'
                        : 'bg-gray-50 border-gray-200 dark:bg-gray-900 dark:border-gray-700 opacity-60'
                    )}
                    onClick={() => toggleBatchStep(step.id)}
                    title={step.description}
                  >
                    {batchResearchSteps.includes(step.id) ? (
                      <CheckSquare className="h-4 w-4 text-green-600" />
                    ) : (
                      <Square className="h-4 w-4 text-gray-400" />
                    )}
                    <span className="text-sm">{step.label}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Category Selection */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-sm font-medium">Categories to Research</Label>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={handleSelectUnresearchedCategories}>
                    Select Unresearched
                  </Button>
                  <Button size="sm" variant="outline" onClick={handleSelectAllCategories}>
                    {selectedBatchCategories.length === availableCategories.length ? 'Deselect All' : 'Select All'}
                  </Button>
                </div>
              </div>
              <ScrollArea className="h-[300px] border rounded-lg p-2">
                {availableCategories.length === 0 ? (
                  <div className="text-center text-muted-foreground py-8">
                    No categories available. Import products first.
                  </div>
                ) : (
                  <div className="space-y-1">
                    {availableCategories.map(cat => {
                      const isSelected = selectedBatchCategories.includes(cat.categoryPath);
                      const progress = batchResearchJob?.progressDetails?.[cat.categoryPath];

                      return (
                        <div
                          key={cat.categoryPath}
                          className={cn(
                            'flex items-center justify-between p-2 rounded cursor-pointer transition-all',
                            isSelected
                              ? 'bg-blue-50 dark:bg-blue-950/30'
                              : 'hover:bg-gray-50 dark:hover:bg-gray-900'
                          )}
                          onClick={() => toggleBatchCategory(cat.categoryPath)}
                        >
                          <div className="flex items-center gap-3">
                            <Checkbox
                              checked={isSelected}
                              onCheckedChange={() => toggleBatchCategory(cat.categoryPath)}
                            />
                            <div>
                              <div className="font-medium text-sm">{cat.categoryName}</div>
                              <div className="text-xs text-muted-foreground">{cat.productCount} products</div>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {cat.hasResearch ? (
                              <Badge variant="default" className="bg-green-600 text-xs">
                                Researched
                              </Badge>
                            ) : (
                              <Badge variant="outline" className="text-xs">
                                Not researched
                              </Badge>
                            )}
                            {progress && (
                              <div className="flex items-center gap-1">
                                {Object.entries(progress.steps || {}).map(([step, status]) => (
                                  <div
                                    key={step}
                                    className={cn(
                                      'w-2 h-2 rounded-full',
                                      status === 'completed' ? 'bg-green-500' :
                                      status === 'running' ? 'bg-blue-500 animate-pulse' :
                                      status === 'failed' ? 'bg-red-500' :
                                      'bg-gray-300'
                                    )}
                                    title={`${step}: ${status}`}
                                  />
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </ScrollArea>
              <div className="mt-2 text-sm text-muted-foreground">
                {selectedBatchCategories.length} of {availableCategories.length} categories selected
              </div>
            </div>

            {/* Job Progress */}
            {batchResearchJob && (
              <Card className={cn(
                'p-4',
                batchResearchJob.status === 'running' ? 'border-blue-200 bg-blue-50 dark:bg-blue-950/20' :
                batchResearchJob.status === 'completed' ? 'border-green-200 bg-green-50 dark:bg-green-950/20' :
                batchResearchJob.status === 'failed' ? 'border-red-200 bg-red-50 dark:bg-red-950/20' :
                ''
              )}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    {batchResearchJob.status === 'running' && <Loader2 className="h-4 w-4 animate-spin" />}
                    {batchResearchJob.status === 'completed' && <CheckCircle className="h-4 w-4 text-green-600" />}
                    {batchResearchJob.status === 'failed' && <XCircle className="h-4 w-4 text-red-600" />}
                    <span className="font-medium capitalize">{batchResearchJob.status}</span>
                  </div>
                  <span className="text-sm">
                    {batchResearchJob.completedCategories}/{batchResearchJob.totalCategories} complete
                  </span>
                </div>
                <Progress value={batchResearchJob.progressPercent} className="h-2" />
                {batchResearchJob.lastError && (
                  <div className="mt-2 text-sm text-red-600">
                    Last error: {batchResearchJob.lastError}
                  </div>
                )}
              </Card>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setBatchResearchDialogOpen(false)}>
              Close
            </Button>
            {batchResearchJob?.status === 'running' ? (
              <Button variant="destructive" onClick={handleCancelBatchResearch}>
                <Square className="h-4 w-4 mr-2" />
                Cancel Job
              </Button>
            ) : (
              <Button
                onClick={handleStartBatchResearch}
                disabled={selectedBatchCategories.length === 0 || batchResearchSteps.length === 0 || batchResearchJob?.status === 'running' || startingBatchResearch}
              >
                {startingBatchResearch ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Starting...
                  </>
                ) : (
                  <>
                    <Play className="h-4 w-4 mr-2" />
                    Start Research ({selectedBatchCategories.length} categories)
                  </>
                )}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Knowledge Chat Dialog */}
      <Dialog open={chatDialogOpen} onOpenChange={(open) => {
        setChatDialogOpen(open);
        if (!open) {
          setChatMessage('');
          setChatParsedIntent(null);
          setChatHistory([]);
        }
      }}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageSquare className="h-5 w-5" />
              Talk to Knowledge
            </DialogTitle>
            <DialogDescription>
              Describe what you want to change in natural language. For example: "Add Unicycle as a product type" or "Remove Gravel Bike".
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Chat History */}
            {chatHistory.length > 0 && (
              <ScrollArea className="h-48 rounded-md border p-3">
                <div className="space-y-3">
                  {chatHistory.map((msg, idx) => (
                    <div key={idx} className={cn(
                      'p-2 rounded-lg text-sm',
                      msg.role === 'user' ? 'bg-primary/10 ml-8' : 'bg-muted mr-8'
                    )}>
                      {msg.content}
                      {msg.result && (
                        <div className="mt-2 p-2 bg-green-50 dark:bg-green-950/30 rounded text-green-700 dark:text-green-400 text-xs">
                          {msg.result.success ? (
                            <div className="flex items-center gap-1">
                              <CheckCircle className="h-3 w-3" />
                              {msg.result.message}
                            </div>
                          ) : (
                            <div className="flex items-center gap-1 text-red-600">
                              <XCircle className="h-3 w-3" />
                              {msg.result.message || msg.result.error}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </ScrollArea>
            )}

            {/* Parsed Intent Confirmation */}
            {chatParsedIntent && (
              <Alert>
                <Wand2 className="h-4 w-4" />
                <AlertDescription>
                  <div className="font-medium mb-1">{chatParsedIntent.confirmationMessage}</div>
                  <div className="text-xs text-muted-foreground">
                    Operation: <code>{chatParsedIntent.operation}</code> |
                    Type: <code>{chatParsedIntent.conceptType}</code> |
                    Name: <code>{chatParsedIntent.conceptName}</code>
                  </div>
                </AlertDescription>
              </Alert>
            )}

            {/* Input */}
            <div className="flex gap-2">
              <Input
                placeholder="e.g., Add Unicycle as a product type"
                value={chatMessage}
                onChange={(e) => setChatMessage(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey && chatMessage.trim()) {
                    e.preventDefault();
                    handleChatSend();
                  }
                }}
                disabled={chatLoading || chatExecuting}
              />
              <Button
                onClick={handleChatSend}
                disabled={!chatMessage.trim() || chatLoading || chatExecuting}
              >
                {chatLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <ArrowRight className="h-4 w-4" />
                )}
              </Button>
            </div>
          </div>

          {chatParsedIntent && (
            <DialogFooter>
              <Button variant="outline" onClick={() => setChatParsedIntent(null)}>
                Cancel
              </Button>
              <Button onClick={handleChatExecute} disabled={chatExecuting}>
                {chatExecuting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Executing...
                  </>
                ) : (
                  <>
                    <Check className="h-4 w-4 mr-2" />
                    {chatParsedIntent.operation === 'add_concept' ? 'Add' :
                     chatParsedIntent.operation === 'remove_concept' ? 'Remove' : 'Update'} {chatParsedIntent.conceptName}
                  </>
                )}
              </Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
